import * as React from 'react';
import { connect } from 'react-redux';
import { Row, Col, Input, InputNumber, Select, DatePicker, Radio, Checkbox, Button, Tabs, Table, Card, Typography, Spin, Alert, message, AutoComplete, Space } from 'antd';
import { DeleteOutlined, CaretRightOutlined, CaretDownOutlined, TagOutlined, HistoryOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { CalculatorState } from './state';
import * as actions from './actions';
import * as pricingEngineApi from 'app/shared/api/pricingEngineApi';
import DateHelper from 'app/helpers/dateHelper';
import { getAgentOfficesById } from 'app/shared/api/searchAgents';
import { getActiveDivisions } from 'app/shared/api/getDivision';
import { fetchAgentsByType } from './loadPage';
import { validatePromotionCodeFormat } from 'app/shared/validation/promotionCode';
import {
  removeSelectedDivisionId,
  setSelectedDivisionId,
} from 'app/shared/division/selectedDivisionStorage';
import {calculateAgeFromDateOfBirth, calculateDateOfBirthFromAge} from './studentAgeDobUtils';
import {
    // loadAdminOfferingsForCalculator, // kept in loadOfferings.ts for rollback
    weeksFilterForProductType,
} from './loadOfferings';
import './Page.css';

const { Title, Text } = Typography;

function isEarlyBirdDiscountType(rawType: unknown): boolean {
    const t = String(rawType ?? '')
        .toLowerCase()
        .replace(/[^a-z]/g, '');
    return t === 'earlybird';
}

function normalizeDiscountKind(rawType: unknown): 'earlybird' | 'market' | 'specialoffer' | 'other' {
    const t = String(rawType ?? '')
        .toLowerCase()
        .replace(/[^a-z]/g, '');
    if (t === 'earlybird') return 'earlybird';
    if (t === 'marketdiscount' || t === 'market') return 'market';
    if (t === 'specialoffer') return 'specialoffer';
    return 'other';
}

function extractApiWarnings(response: unknown): string[] {
    const raw = (response as any)?.Warnings ?? (response as any)?.warnings;
    if (!Array.isArray(raw)) return [];

    const seen = new Set<string>();
    const messages: string[] = [];

    raw.forEach((item) => {
        const message =
            typeof item === 'string'
                ? item.trim()
                : String(item?.Message ?? item?.message ?? '').trim();
        if (!message || seen.has(message)) return;
        seen.add(message);
        messages.push(message);
    });

    return messages;
}

function getDiscountBadgePillClass(type: string): string {
    const k = normalizeDiscountKind(type);
    if (k === 'earlybird') return 'summary-discount-badge summary-discount-badge-early-bird';
    if (k === 'market') return 'summary-discount-badge summary-discount-badge-market';
    if (k === 'specialoffer') return 'summary-discount-badge summary-discount-badge-special-offer';
    return 'summary-discount-badge summary-discount-badge-default-pill';
}

function earlyBirdFieldsFromDiscountRow(d: any): { earlyBird: true; earlyBirdName?: string } {
    const rawName = d?.DiscountName ?? d?.Name ?? d?.name;
    const trimmed = typeof rawName === 'string' ? rawName.trim() : '';
    return {
        earlyBird: true,
        ...(trimmed ? { earlyBirdName: trimmed } : {}),
    };
}

function periodOverlapsDiscountDates(
    periodStart: string,
    periodEnd: string,
    dates: Array<{ From: string; To: string }> | undefined
): boolean {
    if (!dates?.length) return false;
    const p0 = String(periodStart).slice(0, 10);
    const p1 = String(periodEnd).slice(0, 10);
    return dates.some((d) => {
        const d0 = String(d.From).slice(0, 10);
        const d1 = String(d.To).slice(0, 10);
        return d0 <= p1 && d1 >= p0;
    });
}

function getEarlyBirdPeriodFields(
    period: any,
    productOrFeeLevelDiscounts?: any[]
): { earlyBird?: boolean; earlyBirdName?: string } {
    const list = period?.Discounts;
    if (Array.isArray(list)) {
        const eb = list.find((d: any) =>
            isEarlyBirdDiscountType(d?.DiscountType ?? d?.Type ?? d?.discountType)
        );
        if (eb) return earlyBirdFieldsFromDiscountRow(eb);
    }
    if (!productOrFeeLevelDiscounts?.length) return {};
    const eb = productOrFeeLevelDiscounts.find((d: any) =>
        isEarlyBirdDiscountType(d?.Type ?? d?.DiscountType ?? d?.discountType)
    );
    if (
        !eb ||
        !periodOverlapsDiscountDates(period.StartDate, period.EndDate, eb.Dates)
    ) {
        return {};
    }
    return earlyBirdFieldsFromDiscountRow(eb);
}

function parseMoneyAmount(value: string | number | undefined | null): number {
    if (value == null) return 0;
    return parseFloat(String(value).replace(/[^\d.-]/g, '') || '0');
}

function changeStatusForPriceBreakdown(period: { ChangeStatus?: string; DiscountTotal?: string }): string | undefined {
    const status = String(period.ChangeStatus ?? '').trim();
    if (!status) return undefined;
    const normalized = status.replace(/\s+/g, '').toLowerCase();
    if (normalized === 'newprice') {
        return parseMoneyAmount(period.DiscountTotal) > 0 ? 'Discounted Price' : undefined;
    }
    return status;
}

function legacyGrossFromBaseAndDiscount(p: { BasePrice?: string; DiscountTotal?: string }): number {
    const baseVal = parseMoneyAmount(p.BasePrice);
    const discountVal = parseMoneyAmount(p.DiscountTotal);
    const isSwapped = discountVal > baseVal * 0.5;
    return isSwapped ? discountVal : Math.max(0, baseVal - discountVal);
}

function grossFromProductOrFee(p: {
    BasePrice?: string;
    DiscountTotal?: string;
    DatePeriods?: Array<{ PriceAfterDiscounts?: string | number }>;
}): number {
    const periods = p.DatePeriods;
    if (!periods || periods.length === 0) {
        return legacyGrossFromBaseAndDiscount(p);
    }
    const hasExplicitAfterDiscount = periods.some(period => {
        const v = period?.PriceAfterDiscounts;
        if (v == null) return false;
        return String(v).trim() !== '';
    });
    if (!hasExplicitAfterDiscount) {
        return legacyGrossFromBaseAndDiscount(p);
    }
    return periods.reduce((sum, period) => sum + parseMoneyAmount(period?.PriceAfterDiscounts), 0);
}

function SummaryEarlyBirdInline(props: { earlyBird?: boolean; earlyBirdName?: string }) {
    if (!props.earlyBird) return null;
    return (
        <span className="summary-early-bird-inline">
            {props.earlyBirdName ? (
                <span className="summary-early-bird-name">{props.earlyBirdName}</span>
            ) : null}
            <span className="summary-early-bird-tag">
                <TagOutlined className="summary-early-bird-tag-icon" aria-hidden />
                Early Bird
            </span>
        </span>
    );
}

function SummaryChangeStatusInline(props: { changeStatus?: string }) {
    const changeStatus = String(props.changeStatus ?? '').trim();
    if (!changeStatus) return null;
    return (
        <span className="summary-change-status-badge">
            <HistoryOutlined className="summary-change-status-badge-icon" aria-hidden />
            {changeStatus}
        </span>
    );
}

interface Props {
    state: CalculatorState;
    dispatch: (action: any) => void;
}

interface ProductOption {
    value: string;
    label: string;
    code?: string;
    accommodationType?: 'Residence' | 'Homestay';
    accommodationName?: string | null;
    disabled?: boolean;
}

interface ComponentState {
    availableProducts: ProductOption[];
    loadingProducts: boolean;
    packageExpandedIndices: number[];
    loadingDivisions: boolean;
}

class CalculatorPage extends React.Component<Props, ComponentState> {
    constructor(props: Props) {
        super(props);
        this.state = {
            availableProducts: [],
            loadingProducts: false,
            packageExpandedIndices: [],
            loadingDivisions: false,
        };
    }

    private lastDivisionIdUsed: number | undefined = undefined;
    private loadProductsRequestId = 0;

    private resolveSchoolCode(school: { id?: string | number; code?: string } | undefined, fallback: string): string {
        const code = String((school as any)?.code ?? '').trim();
        if (code) return code;
        return String(fallback).replace(/^School-/i, '').trim();
    }

    private getSchoolIdsForProductLoad(): string[] {
        const { state } = this.props;
        if (state.schoolId) {
            const school = state.schools.find(s => String(s.id) === state.schoolId);
            return [this.resolveSchoolCode(school as any, state.schoolId)];
        }
        if (state.centreId) {
            const selectedCentre = state.centres.find(c => String(c.id) === state.centreId);
            if (selectedCentre) {
                const centreCode = (selectedCentre as any).code || String(selectedCentre.id);
                return state.schools
                    .filter(school => {
                        const schoolCentreCode = (school as any).centreCode;
                        if (!schoolCentreCode) return false;
                        return String(schoolCentreCode).toUpperCase() === String(centreCode).toUpperCase();
                    })
                    .map(school => this.resolveSchoolCode(school as any, String(school.id)))
                    .filter(Boolean);
            }
        }
        return [];
    }

    componentDidMount() {
        this.loadProducts();
    }

    private hasDivisionSelected(): boolean {
        const raw = this.props.state.divisionId;
        if (raw === undefined || raw === null) return false;
        return String(raw).trim() !== '';
    }

    private getDivisionId(): number | undefined {
        return pricingEngineApi.parseDivisionIdToNumber(this.props.state.divisionId);
    }

    componentDidUpdate(prevProps: Props) {
        if (prevProps.state.selectedProductType !== this.props.state.selectedProductType) {
            this.setState({ 
                availableProducts: [],
                loadingProducts: true 
            });
            this.props.dispatch(actions.updateProductSearchTerm(''));
            this.loadProducts();
        }
        
        if (prevProps.state.centreId !== this.props.state.centreId ||
            prevProps.state.schoolId !== this.props.state.schoolId) {
            this.loadProducts();
            setTimeout(() => this.trySetCurrencyFromCentreOrSchool(), 0);
        }
        
        if ((prevProps.state.currencies.length === 0 && this.props.state.currencies.length > 0) || 
            (prevProps.state.centres.length === 0 && this.props.state.centres.length > 0)) {
            if (this.props.state.destinationCountryId && !this.props.state.currencyId) {
                setTimeout(() => {
                    this.autoSelectCurrencyForDestinationCountry();
                }, 100);
            }
            if (!this.props.state.destinationCountryId && (this.props.state.centreId || this.props.state.schoolId)) {
                setTimeout(() => this.trySetCurrencyFromCentreOrSchool(), 100);
            }
        }

        if (prevProps.state.summary?.package !== this.props.state.summary?.package) {
            this.setState({ packageExpandedIndices: [] });
        }

        const divisionId = this.getDivisionId();
        if ((divisionId && divisionId !== this.lastDivisionIdUsed && !this.state.loadingProducts) ||
            prevProps.state.bookingSystemId !== this.props.state.bookingSystemId ||
            prevProps.state.schools.length !== this.props.state.schools.length ||
            prevProps.state.weeks !== this.props.state.weeks ||
            prevProps.state.dateFrom !== this.props.state.dateFrom ||
            prevProps.state.dateTo !== this.props.state.dateTo) {
            this.loadProducts();
        }
    }

    getCurrencyCodeFromCentre = (centre: any): string | undefined => {
        if (centre.currencyCode) return String(centre.currencyCode).trim().toUpperCase();
        const currencyVal = centre.currency;
        if (currencyVal) {
            if (typeof currencyVal === 'string') {
                const s = currencyVal.trim();
                if (/^[A-Z]{3}$/i.test(s)) return s.toUpperCase();
                const match = /\(([A-Za-z]{3})\)/.exec(s);
                if (match) return match[1].toUpperCase();
            }
            if (typeof currencyVal === 'object') {
                const code = currencyVal.Code || currencyVal.code;
                if (code) return String(code).trim().toUpperCase();
            }
        }
        if (centre.countryCode) {
            const code = this.getCountryCurrencyMap()[String(centre.countryCode).toUpperCase()];
            return code;
        }
        return undefined;
    };

    trySetCurrencyFromCentreOrSchool = () => {
        const { state, dispatch } = this.props;
        if (state.currencies.length === 0) return;
        const getCurrencyCodeFromCentre = this.getCurrencyCodeFromCentre;
        const findCurrencyByCode = (currencyCode: string) =>
            state.currencies.find((c: any) => c.code && String(c.code).toUpperCase() === String(currencyCode).toUpperCase());
        if (state.centreId) {
            const centre = state.centres.find((c: any) => String(c.id) === state.centreId);
            if (centre) {
                const code = getCurrencyCodeFromCentre(centre);
                if (code) {
                    const match = findCurrencyByCode(code);
                    if (match) {
                        dispatch(actions.updateCurrency(String(match.id)));
                        return;
                    }
                }
            }
        }
        if (state.schoolId) {
            const school = state.schools.find((s: any) => String(s.id) === state.schoolId);
            if (school) {
                const schoolCurrencyCode = (school as any).currencyCode;
                if (schoolCurrencyCode) {
                    const match = findCurrencyByCode(schoolCurrencyCode);
                    if (match) {
                        dispatch(actions.updateCurrency(String(match.id)));
                        return;
                    }
                }
                const schoolCentreCode = (school as any).centreCode;
                if (schoolCentreCode && state.centres.length > 0) {
                    const centre = state.centres.find((c: any) => {
                        const cCode = (c as any).code || String(c.id);
                        return cCode && String(cCode).toUpperCase() === String(schoolCentreCode).toUpperCase();
                    });
                    if (centre) {
                        const code = getCurrencyCodeFromCentre(centre);
                        if (code) {
                            const match = findCurrencyByCode(code);
                            if (match) {
                                dispatch(actions.updateCurrency(String(match.id)));
                                return;
                            }
                        }
                    }
                }
                // Fallback: country-based currency
                const countryCode = (school as any).countryCode;
                if (countryCode) {
                    const code = this.getCountryCurrencyMap()[String(countryCode).toUpperCase()];
                    if (code) {
                        const match = findCurrencyByCode(code);
                        if (match) {
                            dispatch(actions.updateCurrency(String(match.id)));
                            return;
                        }
                    }
                }
            }
        }
    }

    loadProducts = async () => {
        const { state } = this.props;
        const productType = state.selectedProductType;
        const requestId = ++this.loadProductsRequestId;
        const divisionId = this.getDivisionId();
        const bookingSystem = state.bookingSystemId
            ? state.bookingSystems.find(bs => String(bs.id) === state.bookingSystemId)
            : undefined;
        const pricingApiKey = bookingSystem ? pricingEngineApi.getBookingSystemApiKey(bookingSystem.name) : undefined;

        this.setState({ 
            loadingProducts: true,
            availableProducts: [] 
        });
        
        try {
            if (divisionId == null || !pricingApiKey) {
                this.lastDivisionIdUsed = undefined;
                if (requestId !== this.loadProductsRequestId) return;
                if (this.props.state.selectedProductType !== productType) return;
                this.setState({ availableProducts: [], loadingProducts: false });
                return;
            }
            this.lastDivisionIdUsed = divisionId;

            const schoolIds = this.getSchoolIdsForProductLoad();

            if (schoolIds.length === 0) {
                if (requestId !== this.loadProductsRequestId) return;
                if (this.props.state.selectedProductType !== productType) return;
                this.setState({ availableProducts: [], loadingProducts: false });
                return;
            }

            // Admin offerings kept for easy rollback; Pricing API is the sole source for now.
            // const adminOfferings = await loadAdminOfferingsForCalculator({
            //     divisionId,
            //     schoolCodes: schoolIds,
            //     centreId: state.centreId,
            //     productType,
            //     dateFrom: state.dateFrom,
            // }).catch((err) => {
            //     console.warn('Calculator: failed to load Admin offerings', err);
            //     return [] as Array<{ productId: string; name?: string }>;
            // });

            const peOfferings = await pricingEngineApi
                .getProductOfferingsFromPricingApi(
                    schoolIds,
                    productType,
                    divisionId,
                    pricingApiKey,
                    weeksFilterForProductType(productType, state.weeks),
                    state.dateFrom,
                    state.dateTo
                )
                .catch((err) => {
                    console.warn('Calculator: failed to load Pricing Engine offerings', err);
                    return [] as Array<{ productId: string; name?: string }>;
                });

            const mergedById = new Map<string, { productId: string; name?: string }>();
            // Previously: for (const o of [...adminOfferings, ...peOfferings])
            for (const o of peOfferings) {
                const pid = String(o.productId);
                if (!pid) continue;
                const existing = mergedById.get(pid);
                if (!existing) {
                    mergedById.set(pid, o);
                } else if (!existing.name && o.name) {
                    mergedById.set(pid, { ...existing, name: o.name });
                }
            }

            if (productType === 'Package') {
                const productOptions: ProductOption[] = [...mergedById.values()]
                    .map((o) => ({
                        value: String(o.productId),
                        label: o.name || `Package ${o.productId}`,
                        code: String(o.productId),
                        accommodationType: undefined,
                    }))
                    .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
                if (requestId !== this.loadProductsRequestId) return;
                if (this.props.state.selectedProductType !== productType) return;
                this.setState({ availableProducts: productOptions, loadingProducts: false });
                return;
            }

            if (productType === 'Course') {
                const productOptions: ProductOption[] = [...mergedById.values()]
                    .map((o) => ({
                        value: String(o.productId),
                        label: o.name || `Course ${o.productId}`,
                        code: String(o.productId),
                        accommodationType: undefined,
                    }))
                    .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
                if (requestId !== this.loadProductsRequestId) return;
                if (this.props.state.selectedProductType !== productType) return;
                this.setState({ availableProducts: productOptions, loadingProducts: false });
                return;
            }
            
            if (productType === 'Accommodation') {
                const productOptions: ProductOption[] = [...mergedById.values()].map((o) => {
                    const nameLower = (o.name || '').toLowerCase();
                    let accommodationType: 'Residence' | 'Homestay' | undefined = undefined;
                    if (nameLower.includes('homestay') || nameLower.includes('home stay') || nameLower.includes('host family')) {
                        accommodationType = 'Homestay';
                    } else if (nameLower.includes('residence') || nameLower.includes('twin') || nameLower.includes('single')) {
                        accommodationType = 'Residence';
                    }
                    const roomLabel = o.name || `Accommodation ${o.productId}`;
                    return {
                        value: String(o.productId),
                        label: roomLabel,
                        code: String(o.productId),
                        accommodationType,
                        accommodationName: undefined,
                    };
                }).sort((a, b) => (a.label || '').localeCompare(b.label || ''));

                const seen = new Set<string>();
                const dedupedOptions = productOptions.filter(opt => {
                    const key = String(opt.code ?? opt.value);
                    if (!key) return false;
                    if (seen.has(key)) return false;
                    seen.add(key);
                    return true;
                });
                if (requestId !== this.loadProductsRequestId) return;
                if (this.props.state.selectedProductType !== productType) return;
                this.setState({ availableProducts: dedupedOptions, loadingProducts: false });
                return;
            }
            
            if (productType === 'Add-ons') {
                const productOptions: ProductOption[] = [...mergedById.values()]
                    .map((o) => ({
                        value: String(o.productId),
                        label: o.name || `Add-on ${o.productId}`,
                        code: String(o.productId),
                        accommodationType: undefined,
                    }))
                    .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
                if (requestId !== this.loadProductsRequestId) return;
                if (this.props.state.selectedProductType !== productType) return;
                this.setState({ availableProducts: productOptions, loadingProducts: false });
                return;
            }
            
            if (productType === 'Transfer') {
                const productOptions: ProductOption[] = [...mergedById.values()]
                    .map((o) => ({
                        value: String(o.productId),
                        label: o.name || `Transfer ${o.productId}`,
                        code: String(o.productId),
                        accommodationType: undefined,
                    }))
                    .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
                if (requestId !== this.loadProductsRequestId) return;
                if (this.props.state.selectedProductType !== productType) return;
                this.setState({ availableProducts: productOptions, loadingProducts: false });
                return;
            }
            
            if (requestId !== this.loadProductsRequestId) return;
            if (this.props.state.selectedProductType !== productType) return;
            this.setState({ availableProducts: [], loadingProducts: false });
        } catch (error) {
            if (requestId !== this.loadProductsRequestId) return;
            if (this.props.state.selectedProductType !== productType) return;
            this.setState({ 
                availableProducts: [],
                loadingProducts: false 
            });
        }
    };

    handleCalculate = async () => {
        const { state } = this.props;
        const isAmendmentScenario =
            state.pricingScenario === 'Amendment before Arrival' ||
            state.pricingScenario === 'Amendment after Arrival';
        const studentHasArrived =
            state.pricingScenario === 'Amendment before Arrival'
                ? false
                : state.pricingScenario === 'Amendment after Arrival'
                    ? true
                    : undefined;

        if (isAmendmentScenario && !state.priceLockToken?.trim()) {
            message.error('Price Lock Token is required for amendment scenarios.');
            return;
        }
        
        if (!state.bookingSystemId) {
            message.error('Please select a Booking System');
            return;
        }

        if (!state.agentId) {
            message.error('Please select an Agent');
            return;
        }

        if (!state.countryOfResidenceId) {
            message.error('Please select a Country of residence');
            return;
        }

        if (!this.hasDivisionSelected()) {
            message.error('Please select a Division');
            return;
        }
        const divisionId = this.getDivisionId();
        if (divisionId == null) {
            message.error('Please select a Division');
            return;
        }
        
        if (!state.products || state.products.length === 0) {
            message.error('Please add at least one product');
            return;
        }

        const promotionCodeError = validatePromotionCodeFormat(state.promotionCode ?? '');
        if (promotionCodeError) {
            message.error(promotionCodeError);
            return;
        }

        for (const product of state.products) {
            const from = (product.dateFrom || state.dateFrom || '').trim();
            const to = (product.dateTo || state.dateTo || '').trim();
            const label = product.name || product.code || 'Product';
            if (!from || !to) {
                message.error(`Please set both Date From and Date To for "${label}".`);
                return;
            }
            const dFrom = dayjs(from);
            const dTo = dayjs(to);
            if (!dFrom.isValid() || !dTo.isValid()) {
                message.error(`Invalid dates for "${label}".`);
                return;
            }
            if (dFrom.isAfter(dTo, 'day')) {
                message.error(`Date From must be on or before Date To for "${label}".`);
                return;
            }
        }

        const bookingSystem = state.bookingSystems.find(bs => String(bs.id) === state.bookingSystemId);
        if (!bookingSystem) {
            message.error('Invalid booking system selected');
            return;
        }
        
        const bookingSystemApiKey = pricingEngineApi.getBookingSystemApiKey(bookingSystem.name);
        if (!bookingSystemApiKey) {
            message.error('API key not found for selected booking system');
            return;
        }
        
        this.props.dispatch(actions.setCalculating(true));
        
        try {
            const studentDateOfBirth = state.dateOfBirth;
            
            const productsForApi = state.products.map(product => {
                let productCode = product.code || product.name;
                let numericId = '';
                
                if (productCode && productCode.includes('-')) {
                    const parts = productCode.split('-');
                    if (parts.length > 1) {
                        const numericPart = parts[parts.length - 1];
                        if (/^\d+$/.test(numericPart)) {
                            numericId = numericPart;
                        }
                    }
                } 
                else if( productCode && (productCode.startsWith('R') || productCode.startsWith('H'))){
                    numericId = productCode;
                }
                else if (productCode && /^\d+$/.test(productCode)) {
                    numericId = productCode;
                } else if (product.id && /^\d+$/.test(String(product.id))) {
                    const idStr = String(product.id);
                    if (idStr.length <= 10 && Number(product.id) < 1e12) {
                        numericId = idStr;
                    }
                }
                
                let prefix = '';
                const productType = product.type;
                
                if (productType === 'Course') {
                    prefix = 'C';
                } else if (productType === 'Add-ons') {
                    prefix = 'X';
                } else if (productType === 'Transfer') {
                    prefix = 'T';
                } else if (productType === 'Package') {
                    prefix = 'P';
                }
                
                let formattedProductCode: string;
                if (productType === 'Accommodation') {
                    // Use Product.Code from offerings as-is (e.g. R001 / H001).
                    formattedProductCode = productCode || numericId || '';
                } else {
                    formattedProductCode = (prefix && numericId) ? `${prefix}${numericId}` : (productCode || numericId);
                }
                
                let numberOfUnits = 1;
                let unitType = 'Weeks';
                
                if (productType === 'Transfer' || productType === 'Add-ons') {
                    unitType = 'Number of Items';
                    numberOfUnits = 1;
                } else {
                    unitType = 'Weeks';
                    if (product.dateFrom && product.dateTo) {
                        const startDate = new Date(product.dateFrom);
                        const endDate = new Date(product.dateTo);
                        const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                        numberOfUnits = Math.ceil(diffDays / 7);
                        if (numberOfUnits < 1) {
                            numberOfUnits = 1;
                        }
                    } else if (state.weeks) {
                        numberOfUnits = state.weeks;
                    }
                }

                const school = state.schoolId
                    ? state.schools.find(s => String(s.id) === state.schoolId)
                    : undefined;
                return {
                    DivisionId: divisionId,
                    SchoolCode: this.resolveSchoolCode(school as any, state.schoolId || ''),
                    ProductCode: formattedProductCode,
                    Quantity: {
                        StartDate: product.dateFrom || state.dateFrom || '',
                        EndDate: product.dateTo || state.dateTo || '',
                        NumberOfUnits: numberOfUnits,
                        UnitType: unitType,
                    },
                };
            });
            
            let currencyCode: string | undefined;
            if (state.currencyId) {
                const selectedCurrency = state.currencies.find(
                    currency => String(currency.id) === String(state.currencyId)
                );
                if (selectedCurrency?.code) {
                    currencyCode = selectedCurrency.code;
                } else if (state.currencyId.length === 3 && /^[A-Z]{3}$/i.test(state.currencyId)) {
                    currencyCode = state.currencyId.toUpperCase();
                }
            }
            let agentIdNumber: number | null = null;
            if (state.agentId) {
                const agentIdStr = String(state.agentId);
                let parsed: number;
                
                if (agentIdStr.includes('-')) {
                    const parts = agentIdStr.split('-');
                    const numericPart = parts[parts.length - 1];
                    parsed = parseInt(numericPart, 10);
                } else {
                    parsed = parseInt(agentIdStr, 10);
                }
                
                if (!isNaN(parsed) && parsed > 0) {
                    agentIdNumber = parsed;
                }
            }
            
            let agentOfficeIdNumber: number | null = null;
            if (state.agentOfficeId) {
                const officeIdStr = String(state.agentOfficeId);
                let parsed: number;
                
                if (officeIdStr.includes('-')) {
                    const parts = officeIdStr.split('-');
                    const numericPart = parts[parts.length - 1];
                    parsed = parseInt(numericPart, 10);
                } else {
                    parsed = parseInt(officeIdStr, 10);
                }
                
                if (!isNaN(parsed) && parsed > 0) {
                    agentOfficeIdNumber = parsed;
                }
            }
            const promotionTrimmed = (state.promotionCode ?? '').trim();

            const request: pricingEngineApi.CalculatePriceRequest = {
                Options: {
                    SkipEarlyBird: state.earlyBirds,
                    SkipMarketDiscounts: state.marketDiscount,
                    SkipCommission: state.excludeCommissions,
                    ...(promotionTrimmed ? { PromotionCode: promotionTrimmed } : {}),
                },
                BookingDetails: {
                    BookingCreationDate: state.bookingCreationDate || new Date().toISOString().split('T')[0],
                    BookingChangeDate: isAmendmentScenario ? (state.amendmentDate ?? null) : null,
                    SaleCountryIsoCode: state.countryOfResidenceId || '',
                    AgentId: agentIdNumber,
                    AgentOfficeId: agentOfficeIdNumber,
                    StudentDateOfBirth: studentDateOfBirth,
                    ...(studentHasArrived !== undefined ? { StudentHasArrived: studentHasArrived } : {}),
                    CurrencyCode: currencyCode,
                },
                PriceLockToken: isAmendmentScenario ? (state.priceLockToken?.trim() || undefined) : undefined,
                Products: productsForApi,
            };
            
            const response = await pricingEngineApi.calculatePrice(request, bookingSystemApiKey);
            
            const summary = this.mapApiResponseToSummary(response, state);
            this.props.dispatch(actions.setSummary(summary));
        } catch (error: any) {
            let errorResponse: any = null;
            let errorMessages: string[] = [];
            let invalidProducts: Array<{ name: string; errorMessage: string }> = [];
            
            const errorText = error.message || '';
            const jsonMatch = errorText.match(/\{[\s\S]*\}/);
            if (jsonMatch) {
                try {
                    errorResponse = JSON.parse(jsonMatch[0]);
                } catch (e) {
                }
            }
            
            if (errorResponse) {
                if (errorResponse.ResponseStatus?.Errors) {
                    errorMessages = errorResponse.ResponseStatus.Errors
                        .map((err: any) => err.Message || err.ErrorCode)
                        .filter((msg: string) => msg);
                } else if (errorResponse.ResponseStatus?.Message) {
                    errorMessages = [errorResponse.ResponseStatus.Message];
                }
                
                if (errorResponse.InvalidProducts) {
                    invalidProducts = errorResponse.InvalidProducts.map((ip: any) => ({
                        name: ip.Name || ip.ProductCode || 'Unknown Product',
                        errorMessage: ip.ErrorMessage || 'Invalid product'
                    }));
                }
                
                if (errorResponse.Products && errorResponse.Products.length > 0) {
                    const summary = this.mapApiResponseToSummary(errorResponse, state);
                    this.props.dispatch(actions.setSummary(summary));
                    if (errorMessages.length > 0 || invalidProducts.length > 0) {
                        message.warning('Some products are invalid or have errors. Please check the summary.');
                    }
                    return;
                }
            }
            
            if (errorMessages.length === 0) {
                errorMessages = [error.message || 'Failed to calculate price'];
            }
            
            message.error(errorMessages[0] || 'Failed to calculate price. Please try again.');
            this.props.dispatch(actions.setSummary({
                generalDetails: {},
                invalidProducts,
                errors: errorMessages,
                warnings: errorResponse ? extractApiWarnings(errorResponse) : [],
            }));
        } finally {
            this.props.dispatch(actions.setCalculating(false));
        }
    };
    
    mapApiResponseToSummary = (response: pricingEngineApi.CalculatePriceResponse, state: CalculatorState): any => {
        const bookingSystem = state.bookingSystems.find(bs => bs.id === state.bookingSystemId);
        const bookingSystemName = bookingSystem?.name || state.bookingSystemId;
        
        const countryOfResidence = state.countries.find(c => c.id === state.countryOfResidenceId);
        
        const courseProducts: any[] = [];
        const accommodationProducts: any[] = [];
        const transferProducts: any[] = [];
        const addOnProducts: any[] = [];
        const packageProducts: any[] = [];
        
        if (response.Products) {
            response.Products.forEach(product => {
                const code = product.ProductCode || '';
                if (code.startsWith('C')) {
                    courseProducts.push(product);
                } else if (code.startsWith('R') || code.startsWith('H')) {
                    accommodationProducts.push(product);
                } else if (code.startsWith('T')) {
                    transferProducts.push(product);
                } else if (code.startsWith('X')) {
                    addOnProducts.push(product);
                } else if (code.startsWith('P')) {
                    packageProducts.push(product);
                }
            });
        }
        
        const formatDate = (dateStr: string): string => {
            if (!dateStr) return '';
            const date = new Date(dateStr);
            const day = date.getDate().toString().padStart(2, '0');
            const month = date.toLocaleString('en-US', { month: 'short' });
            const year = date.getFullYear();
            return `${day}-${month}-${year}`;
        };

        const selectedPackageProducts = (state.products || []).filter((p: any) => p.type === 'Package');
        const isAggregatedPackage = (name: string) =>
            !name || name === 'Package' || /^\d+\s*products?$/i.test(name.trim());

        const mapProductToSummary = (products: any[], productType: string): any[] => {
            if (products.length === 0) return [];
            
            if (productType === 'Course' || productType === 'Accommodation' || productType === 'Transfer' || productType === 'AddOns' || productType === 'Package') {
                if (productType === 'Package' && products.length === 1 && selectedPackageProducts.length >= 1) {
                    const aggregated = products[0];
                    if (isAggregatedPackage(aggregated.Name)) {
                        const totalTax = parseFloat(aggregated.TaxAmount?.replace(/[^\d.-]/g, '') || '0');
                        const totalCommission = parseFloat(aggregated.Commission?.replace(/[^\d.-]/g, '') || '0');
                        const grossTotal = grossFromProductOrFee(aggregated);
                        const n = selectedPackageProducts.length;
                        const grossPerProduct = (grossTotal / n).toFixed(2);
                        const commissionPerProduct = (totalCommission / n).toFixed(2);
                        const netPerProduct = Math.max(0, parseFloat(grossPerProduct) - parseFloat(commissionPerProduct)).toFixed(2);
                        const taxPerProduct = (totalTax / n).toFixed(2);
                        const currencyMatch = aggregated.BasePrice?.match(/[A-Z]{3}/);
                        const currency = currencyMatch ? currencyMatch[0] : 'EUR';
                        const priceStr = (val: string) => (currency ? `${val} ${currency}` : val);
                        return selectedPackageProducts.map((p: any) => ({
                            name: p.name || p.code || `Product ${p.id || ''}`,
                            price: priceStr(grossPerProduct),
                            grossAmount: priceStr(grossPerProduct),
                            netAmount: priceStr(netPerProduct),
                            taxAmount: taxPerProduct,
                            commissionAmount: priceStr(commissionPerProduct),
                            taxCode: aggregated.DatePeriods?.[0]?.TaxCode || 'XXXX',
                            priceByDateRange: [],
                            discounts: [],
                            periodDiscounts: [],
                        }));
                    }
                }
                return products.map(p => {
                    const currencyMatch = p.BasePrice?.match(/[A-Z]{3}/);
                    const currency = currencyMatch ? currencyMatch[0] : 'EUR';
                    const commissionVal = parseFloat(p.Commission?.replace(/[^\d.-]/g, '') || '0');
                    const grossVal = grossFromProductOrFee(p);
                    const grossAmount = `${Math.max(0, grossVal).toFixed(2)} ${currency}`;
                    const netAmount = `${Math.max(0, grossVal - commissionVal).toFixed(2)} ${currency}`;
                    return {
                    name: p.Name,
                    price: p.BasePrice,
                    grossAmount,
                    netAmount,
                    taxAmount: p.TaxAmount || '0.00',
                    commissionAmount: `${commissionVal.toFixed(2)} ${currency}`,
                    taxCode: p.DatePeriods?.[0]?.TaxCode || 'XXXX',
                    units: p.NumberOfUnits ? {
                        major: p.NumberOfUnits.MajorUnits,
                        minor: p.NumberOfUnits.MinorUnits,
                        majorPrice: p.DatePeriods?.[0]?.PricePerUnit?.PricePerMajorUnit || '0.00',
                        minorPrice: p.DatePeriods?.[0]?.PricePerUnit?.PricePerMinorUnit || '0.00',
                    } : undefined,
                    priceByDateRange: p.DatePeriods?.map((period: any) => {
                        const before = parseFloat(period.PriceBeforeDiscounts?.replace(/[^\d.-]/g, '') || '0');
                        const apiDiscount = parseFloat(period.DiscountTotal?.replace(/[^\d.-]/g, '') || '0');
                        const priceVal = (before > 0 && apiDiscount > before * 0.5) ? period.DiscountTotal : (period.PriceAfterDiscounts || period.PriceBeforeDiscounts);
                        return {
                            dateFrom: formatDate(period.StartDate),
                            dateTo: formatDate(period.EndDate),
                            price: priceVal,
                            changeStatus: changeStatusForPriceBreakdown(period),
                            ...getEarlyBirdPeriodFields(period, p.Discounts),
                        };
                    }) || [],
                    discounts: (p.Discounts || []).map((discount: any) => ({
                        name: discount.Name,
                        amount: discount.DiscountAmount || `0.00 ${currency}`,
                        type: discount.Type,
                    })),
                    periodDiscounts: [],
                    ...(productType === 'Package' && (p as any).BreakdownLines?.length ? {
                        breakdownLines: (p as any).BreakdownLines.map((line: any) => {
                            const pct = line.Percentage;
                            const pctStr = typeof pct === 'number' ? `${pct}%` : (pct != null ? String(pct) : '');
                            return {
                                percentage: pctStr,
                                type: line.Type ?? '',
                                offeringName: line.OfferingName ?? line.Name ?? '',
                                lineTotal: line.LineTotal ?? '',
                            };
                        }),
                    } : {}),
                };
                });
            } else {
                if (products.length === 1) {
                    const p = products[0];
                    const currencyMatch = p.BasePrice?.match(/[A-Z]{3}/);
                    const currency = currencyMatch ? currencyMatch[0] : 'EUR';
                    const commissionVal = parseFloat(p.Commission?.replace(/[^\d.-]/g, '') || '0');
                    const grossVal = grossFromProductOrFee(p);
                    const grossAmount = `${Math.max(0, grossVal).toFixed(2)} ${currency}`;
                    const netAmount = `${Math.max(0, grossVal - commissionVal).toFixed(2)} ${currency}`;
                    return [{
                        name: p.Name,
                        price: p.BasePrice,
                        grossAmount,
                        netAmount,
                        taxAmount: p.TaxAmount || '0.00',
                        commissionAmount: `${commissionVal.toFixed(2)} ${currency}`,
                        taxCode: p.DatePeriods?.[0]?.TaxCode || 'XXXX',
                        units: p.NumberOfUnits ? {
                            major: p.NumberOfUnits.MajorUnits,
                            minor: p.NumberOfUnits.MinorUnits,
                            majorPrice: p.DatePeriods?.[0]?.PricePerUnit?.PricePerMajorUnit || '0.00',
                            minorPrice: p.DatePeriods?.[0]?.PricePerUnit?.PricePerMinorUnit || '0.00',
                        } : undefined,
                        priceByDateRange: p.DatePeriods?.map((period: any) => {
                            const before = parseFloat(period.PriceBeforeDiscounts?.replace(/[^\d.-]/g, '') || '0');
                            const apiDiscount = parseFloat(period.DiscountTotal?.replace(/[^\d.-]/g, '') || '0');
                            const priceVal = (before > 0 && apiDiscount > before * 0.5) ? period.DiscountTotal : (period.PriceAfterDiscounts || period.PriceBeforeDiscounts);
                            return {
                                dateFrom: formatDate(period.StartDate),
                                dateTo: formatDate(period.EndDate),
                                price: priceVal,
                                changeStatus: changeStatusForPriceBreakdown(period),
                                ...getEarlyBirdPeriodFields(period, p.Discounts),
                            };
                        }) || [],
                        discounts: (p.Discounts || []).map((discount: any) => ({
                            name: discount.Name,
                            amount: discount.DiscountAmount || `0.00 ${currency}`,
                            type: discount.Type,
                        })),
                        periodDiscounts: [],
                    }];
                }
                const currency = products[0]?.BasePrice?.match(/[A-Z]{3}/)?.[0] || 'EUR';
                const totalCommission = products.reduce((sum, p) => sum + parseFloat(p.Commission?.replace(/[^\d.-]/g, '') || '0'), 0);
                const grossTotal = products.reduce((sum, p) => sum + grossFromProductOrFee(p), 0);
                const netTotal = Math.max(0, grossTotal - totalCommission);
                return [{
                    name: `${products.length} products`,
                    price: `${grossTotal.toFixed(2)} ${currency}`,
                    grossAmount: `${grossTotal.toFixed(2)} ${currency}`,
                    netAmount: `${netTotal.toFixed(2)} ${currency}`,
                    taxAmount: products.reduce((sum, p) => sum + parseFloat(p.TaxAmount?.replace(/[^\d.-]/g, '') || '0'), 0).toFixed(2) + ' ' + currency,
                    commissionAmount: products.reduce((sum, p) => sum + parseFloat(p.Commission?.replace(/[^\d.-]/g, '') || '0'), 0).toFixed(2) + ' ' + currency,
                    priceByDateRange: [],
                    discounts: [],
                    periodDiscounts: [],
                }];
            }
        };
        
        const errors: string[] = [];
        if (response.ResponseStatus?.Errors) {
            response.ResponseStatus.Errors.forEach(err => {
                if (err.Message) errors.push(err.Message);
            });
        }
        if (errors.length === 0 && response.ResponseStatus?.Message) {
            errors.push(response.ResponseStatus.Message);
        }
        
        const invalidProducts = response.InvalidProducts?.map(ip => ({
            name: ip.Name || ip.ProductCode || 'Unknown Product',
            errorMessage: ip.ErrorMessage || 'Invalid product'
        })) || [];
        const warnings = extractApiWarnings(response);

        const course = mapProductToSummary(courseProducts, 'Course');
        const accommodation = mapProductToSummary(accommodationProducts, 'Accommodation');
        const transfer = mapProductToSummary(transferProducts, 'Transfer');
        const addOns = mapProductToSummary(addOnProducts, 'AddOns');
        const packageItems = mapProductToSummary(packageProducts, 'Package');
        const fees = response.Fees ? response.Fees.map(fee => {
                    const feeCurrencyMatch = fee.BasePrice?.match(/[A-Z]{3}/);
                    const feeCurrency = feeCurrencyMatch ? feeCurrencyMatch[0] : 'EUR';
                    const feeCommissionVal = parseFloat(fee.Commission?.replace(/[^\d.-]/g, '') || '0');
                    const feeGrossVal = grossFromProductOrFee(fee);
                    const feeNetVal = Math.max(0, feeGrossVal - feeCommissionVal);
                    const feeLevelDiscounts = ((fee as any).Discounts || []).map((discount: any) => ({
                        name: discount.Name,
                        amount: discount.DiscountAmount,
                        type: discount.Type,
                    }));
                    return {
                name: fee.Name,
                price: fee.BasePrice,
                grossAmount: `${feeGrossVal.toFixed(2)} ${feeCurrency}`,
                netAmount: `${feeNetVal.toFixed(2)} ${feeCurrency}`,
                taxAmount: fee.TaxAmount || '0.00',
                commissionAmount: `${feeCommissionVal.toFixed(2)} ${feeCurrency}`,
                taxCode: fee.DatePeriods?.[0]?.TaxCode || 'XXXX',
                units: fee.NumberOfUnits ? {
                    major: fee.NumberOfUnits.MajorUnits,
                    minor: fee.NumberOfUnits.MinorUnits,
                    majorPrice: fee.DatePeriods?.[0]?.PricePerUnit?.PricePerMajorUnit || '0.00',
                    minorPrice: fee.DatePeriods?.[0]?.PricePerUnit?.PricePerMinorUnit || '0.00',
                } : undefined,
                priceByDateRange: fee.DatePeriods?.map((period: any) => {
                    const before = parseFloat(period.PriceBeforeDiscounts?.replace(/[^\d.-]/g, '') || '0');
                    const apiDiscount = parseFloat(period.DiscountTotal?.replace(/[^\d.-]/g, '') || '0');
                    const priceVal = (before > 0 && apiDiscount > before * 0.5) ? period.DiscountTotal : (period.PriceAfterDiscounts || period.PriceBeforeDiscounts);
                    return {
                        dateFrom: formatDate(period.StartDate),
                        dateTo: formatDate(period.EndDate),
                        price: priceVal,
                        changeStatus: changeStatusForPriceBreakdown(period),
                        ...getEarlyBirdPeriodFields(period, (fee as any).Discounts),
                    };
                }) || [],
                discounts: feeLevelDiscounts,
                periodDiscounts: feeLevelDiscounts.length > 0 ? [] : (fee.DatePeriods || []).flatMap((period: any) =>
                    (period.Discounts || []).map((discount: any) => ({
                        name: discount.DiscountName,
                        amount: discount.DiscountValue,
                        type: discount.DiscountType,
                        dateFrom: formatDate(period.StartDate),
                        dateTo: formatDate(period.EndDate),
                    }))
                ),
                };
                }) : [];

        const allItems = [...course, ...accommodation, ...transfer, ...addOns, ...packageItems, ...fees];
        const currency = (response.BasePrice?.match(/[A-Z]{3}/)?.[0] || 'EUR') as string;
        const totalGrossVal = allItems.reduce((sum, item) => sum + parseFloat(String(item.grossAmount || '').replace(/[^\d.-]/g, '') || '0'), 0);
        const totalCommissionVal = allItems.reduce((sum, item) => sum + parseFloat(String(item.commissionAmount || '').replace(/[^\d.-]/g, '') || '0'), 0);
        const totalGrossPrice = `${Math.max(0, totalGrossVal).toFixed(2)} ${currency}`;
        const totalNetPrice = `${Math.max(0, totalGrossVal - totalCommissionVal).toFixed(2)} ${currency}`;

        const pickTrimmed = (v: unknown): string | undefined =>
            v != null && String(v).trim() !== '' ? String(v).trim() : undefined;
        const rawApplied =
            pickTrimmed(response.AppliedPromotionCode) ?? pickTrimmed(response.PromotionCode);

        return {
            generalDetails: {
                age: state.age,
                dateOfBirth: state.dateOfBirth
                    ? DateHelper.ToDateString(state.dateOfBirth)
                    : undefined,
                countryOfResidence: countryOfResidence?.name,
                bookingCreationDate: state.bookingCreationDate ? formatDate(state.bookingCreationDate) : undefined,
                bookingDates: state.dateFrom && state.dateTo ? `${formatDate(state.dateFrom)} to ${formatDate(state.dateTo)}` : undefined,
                bookingSystem: bookingSystemName,
            },
            course,
            accommodation,
            transfer,
            addOns,
            package: packageItems,
            fees,
            totalPrice: totalNetPrice,
            totalGrossPrice,
            totalNetPrice,
            priceLockToken: response.PriceLockToken,
            ...(rawApplied ? { appliedPromotionCode: rawApplied } : {}),
            invalidProducts,
            errors,
            warnings,
        };
    };

    formatDate = (date?: string): Dayjs | undefined => {
        return date ? dayjs(date) : undefined;
    };

    formatDateString = (date?: Dayjs | null): string | undefined => {
        return date ? date.format('YYYY-MM-DD') : undefined;
    };

    handleWeeksChange = (value: number | null) => {
        if (value == null) {
            this.props.dispatch(actions.updateWeeks(undefined));
            return;
        }

        if (!Number.isFinite(value) || value < 0) {
            return;
        }

        this.props.dispatch(actions.updateWeeks(Math.floor(value)));
    };

    handleAgeChange = (rawValue: string) => {
        if (!rawValue) {
            this.props.dispatch(actions.updateStudentAgeDob(undefined, undefined));
            return;
        }

        const age = parseInt(rawValue, 10);
        if (isNaN(age) || age < 0 || age > 99) {
            return;
        }

        const dateOfBirth = calculateDateOfBirthFromAge(age, this.props.state.dateOfBirth);
        this.props.dispatch(actions.updateStudentAgeDob(age, dateOfBirth));
    };

    handleDateOfBirthChange = (date?: Dayjs) => {
        if (!date) {
            this.props.dispatch(actions.updateStudentAgeDob(undefined, undefined));
            return;
        }

        const dateOfBirth = this.formatDateString(date);
        if (!dateOfBirth) {
            return;
        }

        const age = calculateAgeFromDateOfBirth(dateOfBirth);
        if (age > 99) {
            return;
        }

        this.props.dispatch(actions.updateStudentAgeDob(age, dateOfBirth));
    };

    handleStartAmendmentFromSummary = () => {
        const priceLockToken = this.props.state.summary?.priceLockToken?.trim();
        if (!priceLockToken) {
            message.warning('Price Lock Token is missing from the latest calculation result.');
            return;
        }

        this.props.dispatch(actions.updatePricingScenario('Amendment before Arrival'));
        this.props.dispatch(actions.updateAmendmentDate(dayjs().format('YYYY-MM-DD')));
        this.props.dispatch(actions.updatePriceLockToken(priceLockToken));
        window.scrollTo({ top: 0, behavior: 'smooth' });
        message.success('Amendment fields have been prefilled.');
    };

    handleCopyPriceLockToken = async () => {
        const token = this.props.state.summary?.priceLockToken ?? '';
        if (!token) {
            message.warning('Price Lock Token is missing.');
            return;
        }

        try {
            await navigator.clipboard.writeText(token);
            message.success('Price lock token copied to clipboard.');
        } catch {
            message.error('Failed to copy Price Lock Token.');
        }
    };

    handleBookingSystemChange = async (bookingSystemId: string | undefined) => {
        const { state, dispatch } = this.props;

        dispatch(actions.updateDivision(undefined));
        dispatch(actions.setDivisions([]));
        dispatch(actions.updateBookingSystem(bookingSystemId));

        if (!bookingSystemId) {
            return;
        }

        const bookingSystem = state.bookingSystems.find(bs => String(bs.id) === bookingSystemId);
        if (!bookingSystem) {
            return;
        }
        
        const apiKey = pricingEngineApi.getBookingSystemApiKey(bookingSystem.name);
        if (!apiKey) {
            message.warning(`API key not found for "${bookingSystem.name}". Please check the booking system name matches the configuration.`);
            return;
        }

        this.setState({ loadingDivisions: true });
        try {
            await Promise.all([
                getActiveDivisions()
                    .then((divisions) => {
                        dispatch(actions.setDivisions(
                            divisions.map(div => ({
                                id: String(div.id),
                                name: div.name,
                            }))
                        ));
                    })
                    .catch(() => {
                        dispatch(actions.setDivisions([]));
                    }),
                this.reloadReferenceDataWithKey(apiKey),
            ]);
        } finally {
            this.setState({ loadingDivisions: false });
        }
    };
    
    reloadReferenceDataWithKey = async (apiKey: string) => {
        const { state, dispatch } = this.props;
        
        dispatch(actions.setReferenceDataLoading(true));
        
        try {
            const [countries, currencies, centres, schools] = await Promise.all([
                pricingEngineApi.getPricingEngineCountries(apiKey).catch((err) => {
                    return state.countries;
                }),
                pricingEngineApi.getPricingEngineCurrencies(apiKey).catch((err) => {
                    return state.currencies;
                }),
                pricingEngineApi.getPricingEngineCentres(apiKey).catch((err) => {
                    return state.centres;
                }),
                pricingEngineApi.getPricingEngineSchools(apiKey).catch((err) => {
                    return state.schools;
                }),
            ]);
            
            dispatch(actions.reloadReferenceData(countries, currencies, centres, schools));
        } catch (error) {
            dispatch(actions.setReferenceDataLoading(false));
        }
    };

    // Common country-to-currency mapping (ISO country code to currency code)
    private getCountryCurrencyMap(): Record<string, string> {
        return {
            'US': 'USD', 'GB': 'GBP', 'CA': 'CAD', 'AU': 'AUD', 'NZ': 'NZD',
            'IE': 'EUR', 'FR': 'EUR', 'DE': 'EUR', 'ES': 'EUR', 'IT': 'EUR',
            'PT': 'EUR', 'NL': 'EUR', 'BE': 'EUR', 'AT': 'EUR', 'GR': 'EUR',
            'FI': 'EUR', 'LU': 'EUR', 'MT': 'EUR', 'CY': 'EUR', 'SK': 'EUR',
            'SI': 'EUR', 'EE': 'EUR', 'LV': 'EUR', 'JP': 'JPY', 'CN': 'CNY',
            'KR': 'KRW', 'IN': 'INR', 'BR': 'BRL', 'MX': 'MXN', 'AR': 'ARS',
            'CH': 'CHF', 'SE': 'SEK', 'NO': 'NOK', 'DK': 'DKK', 'PL': 'PLN',
            'CZ': 'CZK', 'HU': 'HUF', 'RO': 'RON', 'BG': 'BGN', 'HR': 'HRK',
            'TR': 'TRY', 'ZA': 'ZAR', 'EG': 'EGP', 'AE': 'AED', 'SA': 'SAR',
            'IL': 'ILS', 'SG': 'SGD', 'MY': 'MYR', 'TH': 'THB', 'PH': 'PHP',
            'ID': 'IDR', 'VN': 'VND', 'HK': 'HKD', 'TW': 'TWD', 'RU': 'RUB',
            'CI': 'XOF', 'GH': 'GHS', 'NG': 'NGN', 'KE': 'KES', 'MA': 'MAD', 'TN': 'TND',
        };
    }

    autoSelectCurrencyForDestinationCountry = () => {
        const { state, dispatch } = this.props;
        
        if (!state.destinationCountryId || state.currencyId) {
            return;
        }
        
        let selectedCountry = state.countries.find(c => String(c.id) === state.destinationCountryId);
        
        if (!selectedCountry && state.destinationCountryId) {
            const storedCountryName = state.destinationCountryId;
            selectedCountry = state.countries.find(c => 
                c.name === storedCountryName || 
                String(c.id) === storedCountryName ||
                c.name?.toLowerCase() === storedCountryName?.toLowerCase()
            );
        }
        
        if (!selectedCountry) {
            return;
        }
        
        const matchingCurrency = this.findMatchingCurrency(selectedCountry, state);
        
        if (matchingCurrency) {
            dispatch(actions.updateCurrency(String(matchingCurrency.id)));
        }
    };

    findMatchingCurrency = (selectedCountry: any, state: CalculatorState): any => {
        let matchingCurrency = null;
        
        if (selectedCountry.code && state.centres.length > 0) {
            const countryCentres = state.centres.filter((centre: any) => {
                const centreCountryCode = centre.countryCode;
                return centreCountryCode && 
                       selectedCountry.code && 
                       String(centreCountryCode).toUpperCase() === String(selectedCountry.code).toUpperCase();
            });
            
            if (countryCentres.length > 0) {
                const currencyCode = this.getCurrencyCodeFromCentre(countryCentres[0]);
                if (currencyCode && state.currencies.length > 0) {
                    matchingCurrency = state.currencies.find((currency: any) => {
                        const currCode = currency.code;
                        return currCode && 
                               String(currCode).toUpperCase() === String(currencyCode).toUpperCase();
                    });
                    if (matchingCurrency) return matchingCurrency;
                }
            }
        }
        
        if (selectedCountry.code && state.currencies.length > 0) {
            const countryCodeUpper = String(selectedCountry.code).toUpperCase();
            matchingCurrency = state.currencies.find((currency: any) => {
                const currCode = currency.code;
                return currCode && String(currCode).toUpperCase() === countryCodeUpper;
            });
            if (matchingCurrency) return matchingCurrency;
        }
        
        if (selectedCountry.code && state.currencies.length > 0) {
            const countryCurrencyMap = this.getCountryCurrencyMap();
            const countryCodeUpper = String(selectedCountry.code).toUpperCase();
            const expectedCurrencyCode = countryCurrencyMap[countryCodeUpper];
            
            if (expectedCurrencyCode) {
                matchingCurrency = state.currencies.find((currency: any) => {
                    const currCode = currency.code;
                    return currCode && 
                           String(currCode).toUpperCase() === String(expectedCurrencyCode).toUpperCase();
                });
                if (matchingCurrency) return matchingCurrency;
            }
        }
        
        if (state.currencies.length > 0) {
            const countryName = selectedCountry.name?.toLowerCase() || '';
            const nameToCurrency: Record<string, string> = {
                'united states': 'USD', 'usa': 'USD', 'america': 'USD',
                'united kingdom': 'GBP', 'uk': 'GBP', 'britain': 'GBP',
                'canada': 'CAD', 'australia': 'AUD', 'new zealand': 'NZD',
                'japan': 'JPY', 'china': 'CNY', 'south korea': 'KRW',
                'switzerland': 'CHF', 'sweden': 'SEK', 'norway': 'NOK',
                'denmark': 'DKK', 'poland': 'PLN', 'czech': 'CZK',
            };
            
            for (const [namePattern, currencyCode] of Object.entries(nameToCurrency)) {
                if (countryName.includes(namePattern)) {
                    matchingCurrency = state.currencies.find((currency: any) => {
                        const currCode = currency.code;
                        return currCode && 
                               String(currCode).toUpperCase() === String(currencyCode).toUpperCase();
                    });
                    if (matchingCurrency) return matchingCurrency;
                }
            }
        }
        
        return matchingCurrency;
    };


    handleCentreChange = (centreId?: string) => {
        const { state, dispatch } = this.props;
        
        dispatch(actions.updateCentre(centreId));
        if (centreId) {
            const selectedCentre = state.centres.find(c => String(c.id) === centreId);
            if (selectedCentre && state.currencies.length > 0) {
                const currencyCode = this.getCurrencyCodeFromCentre(selectedCentre);
                if (currencyCode) {
                    const matchingCurrency = state.currencies.find((currency: any) => {
                        const currCode = currency.code;
                        return currCode &&
                               String(currCode).toUpperCase() === String(currencyCode).toUpperCase();
                    });
                    if (matchingCurrency) {
                        dispatch(actions.updateCurrency(String(matchingCurrency.id)));
                    }
                }
            }
        }
        if (centreId && state.schoolId) {
            const selectedCentre = state.centres.find(c => String(c.id) === centreId);
            if (selectedCentre) {
                const selectedSchool = state.schools.find(s => String(s.id) === state.schoolId);
                if (selectedSchool) {
                    const schoolCentreCode = (selectedSchool as any).centreCode;
                    const centreCode = (selectedCentre as any).code || String(selectedCentre.id);
                    
                    if (schoolCentreCode && centreCode && 
                        String(schoolCentreCode).toUpperCase() !== String(centreCode).toUpperCase()) {
                        dispatch(actions.updateSchool(undefined));
                    } else if (!schoolCentreCode) {
                        dispatch(actions.updateSchool(undefined));
                    }
                }
            }
        } else if (!centreId) {
            dispatch(actions.updateSchool(undefined));
        }
        
        setTimeout(() => {
            this.loadProducts();
        }, 100);
    };

    handleDestinationCountryChange = (countryId?: string) => {
        const { state, dispatch } = this.props;
        
        dispatch(actions.updateDestinationCountry(countryId));
        
        if (countryId && (state.centreId || state.schoolId)) {
            const selectedCountry = state.countries.find(c => String(c.id) === countryId);
            if (selectedCountry) {
                const countryCode = selectedCountry.code || String(selectedCountry.id);
                
                const centreToCountryMap = new Map<string, string>();
                if (state.schools && state.schools.length > 0) {
                    state.schools.forEach(school => {
                        const schoolCentreCode = (school as any).centreCode || 
                                                  ((school as any).Centre && (school as any).Centre.Code) ||
                                                  ((school as any).centre && (school as any).centre.code);
                        const schoolCountryCode = (school as any).countryCode ||
                                                   ((school as any).Country && (school as any).Country.Code) ||
                                                   ((school as any).country && (school as any).country.code);
                        if (schoolCentreCode && schoolCountryCode) {
                            const centreCodeKey = String(schoolCentreCode).toUpperCase().trim();
                            const countryCodeValue = String(schoolCountryCode).toUpperCase().trim();
                            if (centreCodeKey && countryCodeValue) {
                                if (!centreToCountryMap.has(centreCodeKey)) {
                                    centreToCountryMap.set(centreCodeKey, countryCodeValue);
                                }
                            }
                        }
                    });
                }
                
                if (state.centreId && centreToCountryMap.size > 0) {
                    const selectedCentre = state.centres.find(c => String(c.id) === state.centreId);
                    if (selectedCentre) {
                        let centreCountryCode = (selectedCentre as any).countryCode;
                        
                        if (!centreCountryCode) {
                            const centreCode = (selectedCentre as any).code || String(selectedCentre.id);
                            centreCountryCode = centreCode ? centreToCountryMap.get(String(centreCode).toUpperCase().trim()) : undefined;
                        }
                        
                        const countryCodeUpper = String(countryCode).toUpperCase().trim();
                        if (centreCountryCode) {
                            const centreCountryCodeUpper = String(centreCountryCode).toUpperCase().trim();
                            if (centreCountryCodeUpper !== countryCodeUpper) {
                                dispatch(actions.updateCentre(undefined));
                                dispatch(actions.updateSchool(undefined));
                            }
                        }
                    }
                }
                
                if (state.schoolId && !state.centreId) {
                    dispatch(actions.updateSchool(undefined));
                }
            }
        } else if (!countryId) {
            dispatch(actions.updateCentre(undefined));
            dispatch(actions.updateSchool(undefined));
        }
        
        if (countryId) {
            const trySelectCurrency = () => {
                const currentState = this.props.state;
                const selectedCountry = currentState.countries.find(c => String(c.id) === countryId);
                if (selectedCountry) {
                    const matchingCurrency = this.findMatchingCurrency(selectedCountry, currentState);
                    
                    if (matchingCurrency) {
                        this.props.dispatch(actions.updateCurrency(String(matchingCurrency.id)));
                        return true;
                    }
                }
                return false;
            };
            
            if (!trySelectCurrency()) {
                setTimeout(() => {
                    trySelectCurrency();
                }, 200);
            }
        }
    };

    render() {
        const { state } = this.props;
        const isAmendmentScenario =
            state.pricingScenario === 'Amendment before Arrival' ||
            state.pricingScenario === 'Amendment after Arrival';
        const promotionCodeFormatError = validatePromotionCodeFormat(state.promotionCode ?? '');
        const productColumns = [
            {
                title: 'Product Name',
                dataIndex: 'name',
                key: 'name',
                onCell: (record: any) => {
                    if (record.isHeader) {
                        return { colSpan: 4 };
                    }
                    return {};
                },
                render: (text: string, record: any) => {
                    if (record.isHeader) {
                        return <span style={{ fontWeight: 500 }}>{text}</span>;
                    }
                    return text;
                },
            },
            {
                title: 'Date From',
                dataIndex: 'dateFrom',
                key: 'dateFrom',
                onCell: (record: any) => {
                    if (record.isHeader) {
                        return { colSpan: 0 };
                    }
                    return {};
                },
                render: (date: string | undefined, record: any) => {
                    if (record.isHeader) return null;
                    return (
                        <DatePicker
                            placeholder="Select Date"
                            value={date ? dayjs(date) : undefined}
                            format={DateHelper.DATE_PICKER_FORMATS}
                            onChange={(date) => {
                                this.props.dispatch(actions.updateProductDateFrom(record.id, this.formatDateString(date)));
                            }}
                            style={{ width: '100%' }}
                        />
                    );
                },
            },
            {
                title: 'Date To',
                dataIndex: 'dateTo',
                key: 'dateTo',
                onCell: (record: any) => {
                    if (record.isHeader) {
                        return { colSpan: 0 };
                    }
                    return {};
                },
                render: (date: string | undefined, record: any) => {
                    if (record.isHeader) return null;
                    return (
                        <DatePicker
                            placeholder="Select Date"
                            value={date ? dayjs(date) : undefined}
                            format={DateHelper.DATE_PICKER_FORMATS}
                            onChange={(date) => {
                                this.props.dispatch(actions.updateProductDateTo(record.id, this.formatDateString(date)));
                            }}
                            style={{ width: '100%' }}
                        />
                    );
                },
            },
            {
                title: 'Action',
                key: 'action',
                onCell: (record: any) => {
                    if (record.isHeader) {
                        return { colSpan: 0 };
                    }
                    return {};
                },
                render: (_: any, record: any) => {
                    if (record.isHeader) return null;
                    return (
                        <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => this.props.dispatch(actions.removeProduct(record.id))}
                        />
                    );
                },
            },
        ];

        const productsByType = (state.products || []).reduce((acc, product) => {
            if (!acc[product.type]) {
                acc[product.type] = [];
            }
            acc[product.type].push(product);
            return acc;
        }, {} as Record<string, typeof state.products>);

        const tableData: any[] = [];
        const productTypes: Array<'Course' | 'Accommodation' | 'Transfer' | 'Add-ons' | 'Package'> = 
            ['Course', 'Accommodation', 'Transfer', 'Add-ons', 'Package'];
        
        productTypes.forEach(type => {
            const products = productsByType[type] || [];
            if (products.length > 0) {
                tableData.push({
                    id: `header-${type}`,
                    isHeader: true,
                    type: type,
                    name: type,
                });
                products.forEach(product => {
                    tableData.push(product);
                });
            }
        });

        return (
            <div className="calculator-page">
                <Row gutter={24}>
                    <Col span={12}>
                        <Card>
                            <Title level={2}>Calculator</Title>
                            <Text type="secondary">One-page price builder for Course, Accommodation, Transfers and Fees</Text>
                            
                            <div className="calculator-section">
                                <Title level={4}>General Details</Title>
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <div className="form-field">
                                            <label>Booking System</label>
                                            <Select
                                                placeholder="Choose Booking System"
                                                value={state.bookingSystemId}
                                                onChange={(value) => {
                                                    if (value) {
                                                        this.handleBookingSystemChange(value);
                                                    } else {
                                                        this.handleBookingSystemChange(undefined);
                                                    }
                                                }}
                                                style={{ width: '100%' }}
                                                showSearch
                                                allowClear
                                                loading={state.isLoadingReferenceData}
                                                disabled={state.isLoadingReferenceData}
                                                options={state.bookingSystems
                                                    .filter(bs => bs.id != null && bs.id !== undefined)
                                                    .map(bs => ({
                                                        label: bs.name,
                                                        value: String(bs.id),
                                                    }))}
                                                filterOption={(input, option) =>
                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                }
                                            />
                                            {state.isLoadingReferenceData && (
                                                <div style={{ marginTop: 4, fontSize: 12, color: '#1890ff' }}>
                                                    Loading reference data...
                                                </div>
                                            )}
                                        </div>
                                    </Col>
                                    <Col span={12}>
                                        <div className="form-field">
                                            <label>Division</label>
                                            <Select
                                                placeholder={state.bookingSystemId ? 'Choose Division' : 'Select a booking system first'}
                                                value={state.divisionId}
                                                onChange={(value) => {
                                                    if (value == null || value === '') {
                                                        removeSelectedDivisionId();
                                                        this.props.dispatch(actions.updateDivision(undefined));
                                                        return;
                                                    }
                                                    const selectedDivisionId = String(value).trim();
                                                    this.props.dispatch(actions.updateDivision(selectedDivisionId));
                                                    setSelectedDivisionId(selectedDivisionId);
                                                }}
                                                style={{ width: '100%' }}
                                                showSearch
                                                allowClear
                                                loading={this.state.loadingDivisions}
                                                disabled={!state.bookingSystemId || this.state.loadingDivisions}
                                                options={state.divisions
                                                    .filter(d => d.id != null && d.id !== undefined)
                                                    .map(d => ({
                                                        label: d.name,
                                                        value: String(d.id),
                                                    }))}
                                                filterOption={(input, option) =>
                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                }
                                            />
                                        </div>
                                    </Col>
                                </Row>

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <div className="form-field">
                                            <label>Centre</label>
                                            <Select
                                                placeholder="Choose Centre"
                                                value={state.centreId}
                                                onChange={(value) => this.handleCentreChange(value)}
                                                style={{ width: '100%' }}
                                                showSearch
                                                allowClear
                                                disabled={state.centres.length === 0}
                                                options={(() => {
                                                    let filteredCentres = state.centres.filter(centre => centre.id != null && centre.id !== undefined);
                                                    
                                                    if (state.destinationCountryId) {
                                                        const selectedCountry = state.countries.find(c => String(c.id) === String(state.destinationCountryId));
                                                        if (selectedCountry) {
                                                            const countryCode = (selectedCountry.code || String(selectedCountry.id)).toUpperCase().trim();
                                                            
                                                            const centreToCountryMap = new Map<string, string>();
                                                            if (state.schools && state.schools.length > 0) {
                                                                state.schools.forEach(school => {
                                                                    const schoolCentreCode = (school as any).centreCode || 
                                                                                              ((school as any).Centre && (school as any).Centre.Code) ||
                                                                                              ((school as any).centre && (school as any).centre.code);
                                                                    const schoolCountryCode = (school as any).countryCode ||
                                                                                               ((school as any).Country && (school as any).Country.Code) ||
                                                                                               ((school as any).country && (school as any).country.code);
                                                                    if (schoolCentreCode && schoolCountryCode) {
                                                                        const centreCodeKey = String(schoolCentreCode).toUpperCase().trim();
                                                                        const countryCodeValue = String(schoolCountryCode).toUpperCase().trim();
                                                                        if (centreCodeKey && countryCodeValue) {
                                                                            centreToCountryMap.set(centreCodeKey, countryCodeValue);
                                                                        }
                                                                    }
                                                                });
                                                            }
                                                            
                                                            filteredCentres = filteredCentres.filter(centre => {
                                                                let centreCountryCode = (centre as any).countryCode;
                                                                
                                                                if (!centreCountryCode && centreToCountryMap.size > 0) {
                                                                    const centreCode = (centre as any).code || String(centre.id);
                                                                    if (centreCode) {
                                                                        const codeUpper = String(centreCode).toUpperCase().trim();
                                                                        centreCountryCode = centreToCountryMap.get(codeUpper);
                                                                        
                                                                        if (!centreCountryCode && centre.id) {
                                                                            const idUpper = String(centre.id).toUpperCase().trim();
                                                                            centreCountryCode = centreToCountryMap.get(idUpper);
                                                                        }
                                                                    }
                                                                }
                                                                
                                                                if (!centreCountryCode) {
                                                                    return false;
                                                                }
                                                                
                                                                return String(centreCountryCode).toUpperCase().trim() === countryCode;
                                                            });
                                                        }
                                                    }
                                                    
                                                    return filteredCentres
                                                        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                                                        .map(centre => ({
                                                            label: centre.name,
                                                            value: String(centre.id),
                                                        }));
                                                })()}
                                                filterOption={(input, option) =>
                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                }
                                                notFoundContent={state.centres.length === 0 ? 'No centres loaded. Please select a booking system first.' : (state.destinationCountryId ? 'No centres found for selected country' : 'No centres found')}
                                            />
                                        </div>
                                    </Col>
                                    <Col span={12}>
                                        <div className="form-field">
                                            <label>School</label>
                                            <Select
                                                placeholder="Choose School"
                                                value={state.schoolId}
                                                onChange={(value) => this.props.dispatch(actions.updateSchool(value))}
                                                style={{ width: '100%' }}
                                                showSearch
                                                allowClear
                                                disabled={state.schools.length === 0}
                                                notFoundContent={state.isLoadingReferenceData ? 'Loading...' : (state.schools.length === 0 ? 'No schools available' : (state.centreId ? 'No schools found for selected centre' : 'No schools found'))}
                                                loading={state.isLoadingReferenceData}
                                                options={(() => {
                                                    let filteredSchools = state.schools.filter(school => school.id != null && school.id !== undefined);
                                                    
                                                    if (state.centreId) {
                                                        const selectedCentre = state.centres.find(c => String(c.id) === state.centreId);
                                                        if (selectedCentre) {
                                                            const centreCode = (selectedCentre as any).code || String(selectedCentre.id);
                                                            filteredSchools = filteredSchools.filter(school => {
                                                                const schoolCentreCode = (school as any).centreCode;
                                                                if (!schoolCentreCode) return false;
                                                                return String(schoolCentreCode).toUpperCase() === String(centreCode).toUpperCase();
                                                            });
                                                        }
                                                    }
                                                    
                                                    return filteredSchools
                                                        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                                                        .map(school => ({
                                                            label: school.name || 'Unnamed School',
                                                            value: String(school.id),
                                                        }));
                                                })()}
                                                filterOption={(input, option) =>
                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                }
                                            />
                                        </div>
                                    </Col>
                                </Row>

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <div className="form-field">
                                            <label>Currency</label>
                                            <Select
                                                placeholder="Choose Currency"
                                                value={state.currencyId}
                                                onChange={(value) => this.props.dispatch(actions.updateCurrency(value))}
                                                style={{ width: '100%' }}
                                                showSearch
                                                allowClear
                                                options={state.currencies
                                                    .filter(currency => currency.id != null && currency.id !== undefined)
                                                    .map(currency => ({
                                                        label: currency.name,
                                                        value: String(currency.id),
                                                    }))}
                                                filterOption={(input, option) =>
                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                }
                                            />
                                        </div>
                                    </Col>
                                    <Col span={12}>
                                        <div className="form-field">
                                            <label>Booking Creation Date</label>
                                            <DatePicker
                                                placeholder="Date"
                                                value={this.formatDate(state.bookingCreationDate)}
                                                onChange={(date) => {
                                                    this.props.dispatch(actions.updateBookingCreationDate(this.formatDateString(date)));
                                                }}
                                                format={DateHelper.DATE_PICKER_FORMATS}
                                                style={{ width: '100%' }}
                                            />
                                        </div>
                                    </Col>
                                </Row>

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <div className="form-field">
                                            <label>Agent</label>
                                            <Radio.Group
                                                value={state.agentType}
                                                onChange={async (e) => {
                                                    const newAgentType = e.target.value;
                                                    this.props.dispatch(actions.updateAgentType(newAgentType));
                                                    this.props.dispatch(actions.updateAgent(undefined));
                                                    try {
                                                        const agents = await fetchAgentsByType(newAgentType);
                                                        this.props.dispatch(actions.reloadAgents(agents));
                                                    } catch (error: any) {
                                                        this.props.dispatch(actions.reloadAgents([]));
                                                    }
                                                }}
                                                buttonStyle="solid"
                                                style={{ marginBottom: 8, width: '100%', display: 'flex' }}
                                            >
                                                <Radio.Button value="Walk In" style={{ flex: 1, textAlign: 'center' }}>Walk In</Radio.Button>
                                                <Radio.Button value="E-Sale" style={{ flex: 1, textAlign: 'center' }}>E-Sale</Radio.Button>
                                                <Radio.Button value="Agent" style={{ flex: 1, textAlign: 'center' }}>Agent</Radio.Button>
                                            </Radio.Group>
                                            <Select
                                                placeholder="Choose Agent"
                                                value={state.agentId}
                                                onChange={async (value) => {
                                                    this.props.dispatch(actions.updateAgent(value));
                                                    
                                                    if (value) {
                                                        try {
                                                            const agentOfficesData = await getAgentOfficesById([value], true);
                                                            const offices: Array<{ id: string; name: string }> = [];
                                                            agentOfficesData.forEach(agent => {
                                                                if (agent.offices && Array.isArray(agent.offices)) {
                                                                    agent.offices.forEach((office: any) => {
                                                                        offices.push({
                                                                            id: office.id,
                                                                            name: office.name || `Office ${office.id}`
                                                                        });
                                                                    });
                                                                }
                                                            });
                                                            this.props.dispatch(actions.loadAgentOffices(offices));
                                                            this.props.dispatch(actions.updateAgentOffice(undefined));
                                                        } catch (error: any) {
                                                            this.props.dispatch(actions.loadAgentOffices([]));
                                                            this.props.dispatch(actions.updateAgentOffice(undefined));
                                                        }
                                                    } else {
                                                        this.props.dispatch(actions.loadAgentOffices([]));
                                                        this.props.dispatch(actions.updateAgentOffice(undefined));
                                                    }
                                                }}
                                                style={{ width: '100%' }}
                                                showSearch
                                                allowClear
                                                notFoundContent={state.agents.length === 0 ? 'No agents available' : 'No agents found'}
                                                options={state.agents
                                                    .filter(agent => agent.id != null && agent.id !== undefined && agent.name)
                                                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                                                    .map(agent => ({
                                                        label: agent.name || 'Unnamed Agent',
                                                        value: String(agent.id),
                                                    }))}
                                                filterOption={(input, option) =>
                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                }
                                            />
                                        </div>
                                    </Col>
                                    <Col span={12}>
                                        <div className="form-field">
                                            <label>Promo Code</label>
                                            <Input
                                                placeholder="Promo Code"
                                                value={state.promotionCode ?? ''}
                                                status={promotionCodeFormatError ? 'error' : undefined}
                                                onChange={(e) => {
                                                    const v = e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 15);
                                                    this.props.dispatch(actions.updatePromotionCode(v === '' ? undefined : v));
                                                }}
                                                allowClear
                                            />
                                            <div
                                                style={{
                                                    marginTop: 4,
                                                    fontSize: 12,
                                                    color: promotionCodeFormatError ? '#ff4d4f' : 'rgba(0, 0, 0, 0.45)',
                                                }}
                                            >
                                                {promotionCodeFormatError ?? 'Leave blank to apply calculation without promo code'}
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                                {state.agentId && state.agentOffices.length > 0 && (
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <div className="form-field">
                                                <label>Agent Office</label>
                                                <Select
                                                    placeholder="Choose Agent Office"
                                                    value={state.agentOfficeId}
                                                    onChange={(value) => this.props.dispatch(actions.updateAgentOffice(value))}
                                                    style={{ width: '100%' }}
                                                    showSearch
                                                    allowClear
                                                    notFoundContent="No offices found"
                                                    options={state.agentOffices
                                                        .filter(office => office.id != null && office.id !== undefined && office.name)
                                                        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                                                        .map(office => ({
                                                            label: office.name || 'Unnamed Office',
                                                            value: String(office.id),
                                                        }))}
                                                    filterOption={(input, option) =>
                                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                    }
                                                />
                                            </div>
                                        </Col>
                                    </Row>
                                )}

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <div className="form-field">
                                            <label>Country of residence</label>
                                            <Select
                                                placeholder="Choose Country"
                                                value={state.countryOfResidenceId}
                                                onChange={(value) => this.props.dispatch(actions.updateCountryOfResidence(value))}
                                                style={{ width: '100%' }}
                                                showSearch
                                                allowClear
                                                options={state.countries
                                                    .filter(country => country.id != null && country.id !== undefined && country.name)
                                                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                                                    .map(country => ({
                                                        label: country.name,
                                                        value: String(country.id),
                                                    }))}
                                                filterOption={(input, option) =>
                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                }
                                            />
                                        </div>
                                    </Col>
                                    <Col span={12}>
                                        <div className="form-field">
                                            <label>Date From</label>
                                            <DatePicker
                                                placeholder="Select Date"
                                                value={this.formatDate(state.dateFrom)}
                                                onChange={(date) => {
                                                    this.props.dispatch(actions.updateDateFrom(this.formatDateString(date)));
                                                }}
                                                format={DateHelper.DATE_PICKER_FORMATS}
                                                style={{ width: '100%' }}
                                            />
                                        </div>
                                    </Col>
                                </Row>

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <div className="form-field">
                                            <label>Weeks</label>
                                            <InputNumber
                                                min={0}
                                                precision={0}
                                                controls
                                                placeholder="Enter Weeks"
                                                value={state.weeks}
                                                onChange={this.handleWeeksChange}
                                                style={{ width: '100%' }}
                                                onKeyDown={(e) => {
                                                    if (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === 'E') {
                                                        e.preventDefault();
                                                    }
                                                }}
                                            />
                                        </div>
                                    </Col>
                                    <Col span={12}>
                                        <div className="form-field">
                                            <label>Date To</label>
                                            <DatePicker
                                                placeholder="Select Date"
                                                value={this.formatDate(state.dateTo)}
                                                onChange={(date) => {
                                                    this.props.dispatch(actions.updateDateTo(this.formatDateString(date)));
                                                }}
                                                format={DateHelper.DATE_PICKER_FORMATS}
                                                style={{ width: '100%' }}
                                            />
                                        </div>
                                    </Col>
                                </Row>

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <div className="form-field student-age-dob-field">
                                            {state.age == null && !state.dateOfBirth && (
                                                <label>Student&apos;s Age</label>
                                            )}
                                            <Row gutter={8} align="bottom">
                                                <Col flex="80px">
                                                    {(state.age != null || state.dateOfBirth) && <label>Age</label>}
                                                    <Input
                                                        type="number"
                                                        min={0}
                                                        max={99}
                                                        placeholder="Age"
                                                        value={state.age ?? ''}
                                                        onChange={(e) => this.handleAgeChange(e.target.value)}
                                                    />
                                                </Col>
                                                <Col flex="auto">
                                                    {(state.age != null || state.dateOfBirth) && <label>DOB</label>}
                                                    <DatePicker
                                                        placeholder="Select Date"
                                                        value={this.formatDate(state.dateOfBirth)}
                                                        onChange={(date) => this.handleDateOfBirthChange(date ?? undefined)}
                                                        format={DateHelper.DATE_PICKER_FORMATS}
                                                        style={{ width: '100%' }}
                                                        allowClear
                                                    />
                                                </Col>
                                            </Row>
                                        </div>
                                    </Col>
                                </Row>

                                <Row gutter={16}>
                                    <Col span={24}>
                                        <div className="form-field">
                                            <label>Pricing Scenario</label>
                                            <Radio.Group
                                                value={state.pricingScenario ?? 'New booking'}
                                                onChange={(e) => {
                                                    this.props.dispatch(actions.updatePricingScenario(e.target.value));
                                                }}
                                                buttonStyle="solid"
                                                style={{ marginBottom: 8, width: '100%', display: 'flex' }}
                                            >
                                                <Radio.Button value="New booking" style={{ flex: 1, textAlign: 'center' }}>New booking</Radio.Button>
                                                <Radio.Button value="Amendment before Arrival" style={{ flex: 1, textAlign: 'center' }}>Amendment before Arrival</Radio.Button>
                                                <Radio.Button value="Amendment after Arrival" style={{ flex: 1, textAlign: 'center' }}>Amendment after Arrival</Radio.Button>
                                            </Radio.Group>
                                        </div>
                                    </Col>
                                </Row>
                                {isAmendmentScenario && (
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <div className="form-field">
                                                <label>Amendment Date</label>
                                                <DatePicker
                                                    placeholder="Date"
                                                    value={this.formatDate(state.amendmentDate)}
                                                    onChange={(date) => {
                                                        this.props.dispatch(actions.updateAmendmentDate(this.formatDateString(date)));
                                                    }}
                                                    format={DateHelper.DATE_PICKER_FORMATS}
                                                    style={{ width: '100%' }}
                                                />
                                            </div>
                                        </Col>
                                        <Col span={12}>
                                            <div className="form-field">
                                                <label>Price Lock Token</label>
                                                 <Input
                                                    type="text"
                                                    placeholder="Price Lock Token of the booking to update"
                                                    value={state.priceLockToken}
                                                    onChange={(e) => {
                                                        this.props.dispatch(actions.updatePriceLockToken(e.target.value || undefined));
                                                    }}
                                                />
                                            </div>
                                        </Col>
                                    </Row>
                                )}
                            </div>

                            <div className="calculator-section">
                                <Title level={4}>Products</Title>
                                <Tabs
                                    activeKey={state.selectedProductType}
                                    onChange={(key) => this.props.dispatch(actions.updateSelectedProductType(key as any))}
                                    items={[
                                        { key: 'Course', label: 'Course' },
                                        { key: 'Accommodation', label: 'Accommodation' },
                                        { key: 'Transfer', label: 'Transfer' },
                                        { key: 'Add-ons', label: 'Add-ons' },
                                        { key: 'Package', label: 'Package' },
                                    ]}
                                />
                                
                                <div className="form-field" style={{ marginTop: 16 }}>
                                    <AutoComplete
                                        placeholder={this.state.loadingProducts ? "Loading products..." : !this.hasDivisionSelected() ? "Select a division above" : "Select or type product"}
                                        value={state.productSearchTerm}
                                        onChange={(value) => this.props.dispatch(actions.updateProductSearchTerm(value ?? ''))}
                                        onSelect={(value) => {
                                            if (value) {
                                                const productOption = this.state.availableProducts.find(opt => opt.value === value || opt.code === value);
                                                this.props.dispatch(actions.addProduct({
                                                    id: Date.now().toString(),
                                                    name: productOption?.label || productOption?.value || value,
                                                    code: productOption?.code || value,
                                                    type: state.selectedProductType,
                                                    accommodationType: productOption?.accommodationType,
                                                    accommodationName: productOption?.accommodationName ?? undefined,
                                                }));
                                                this.props.dispatch(actions.updateProductSearchTerm(''));
                                            }
                                        }}
                                        options={this.state.loadingProducts ? [] : this.state.availableProducts
                                            .filter(option => {
                                                const label = option?.label ?? '';
                                                const search = (state.productSearchTerm ?? '').toLowerCase();
                                                return String(label).toLowerCase().includes(search);
                                            })
                                            .map(opt => ({ value: opt.value, label: opt.label ?? opt.value }))
                                        }
                                        notFoundContent={this.state.loadingProducts ? 'Loading products...' : (!this.hasDivisionSelected() ? 'Select a division in General Details' : this.state.availableProducts.length === 0 ? 'No products available' : 'No products found')}
                                        style={{ width: '100%' }}
                                        disabled={this.state.loadingProducts}
                                    />
                                </div>

                                <Table
                                    columns={productColumns}
                                    dataSource={tableData}
                                    rowKey="id"
                                    pagination={false}
                                    size="small"
                                    rowClassName={(record: any) => record.isHeader ? 'product-group-header' : ''}
                                />

                                <div className="calculator-options" style={{ marginTop: 16, display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', gap: 16 }}>
                                    <span style={{ fontWeight: 500 }}>Exclude</span>
                                    <Checkbox
                                        checked={state.excludeCommissions}
                                        onChange={(e) => this.props.dispatch(actions.updateExcludeCommissions(e.target.checked))}
                                    >
                                        Commissions
                                    </Checkbox>
                                    <Checkbox
                                        checked={state.earlyBirds}
                                        onChange={(e) => this.props.dispatch(actions.updateEarlyBirds(e.target.checked))}
                                    >
                                        Early Birds
                                    </Checkbox>
                                    <Checkbox
                                        checked={state.marketDiscount}
                                        onChange={(e) => this.props.dispatch(actions.updateMarketDiscount(e.target.checked))}
                                    >
                                        Market Discount
                                    </Checkbox>
                                </div>

                                <Button
                                    type="primary"
                                    size="large"
                                    onClick={this.handleCalculate}
                                    loading={state.isCalculating}
                                    style={{ width: '100%', marginTop: 16 }}
                                >
                                    Calculate
                                </Button>
                            </div>
                        </Card>
                    </Col>

                    <Col span={12}>
                        <Card>
                            <Title className="summary-title" level={2}>Summary</Title>
                            
                            {state.summary ? (
                                <div className="calculator-summary">
                                    
                                    {state.summary?.priceLockToken && 
                                        <React.Fragment key='price-lock-token'>
                                            <div className="summary-token-block">
                                                <b>Price Lock Token</b>
                                                <p className="summary-token-value">{state.summary.priceLockToken}</p>
                                                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                    <Space orientation='horizontal'>
                                                        <Button onClick={this.handleCopyPriceLockToken}>
                                                            Copy Token
                                                        </Button>
                                                        <Button type="primary" onClick={this.handleStartAmendmentFromSummary}>
                                                            Amend Booking
                                                        </Button>
                                                    </Space>
                                                </div>
                                            </div>
                                        </React.Fragment>
                                    }
                                    {state.summary.course && state.summary.course.length > 0 && 
                                        state.summary.course.map((product: any, index: number) => (
                                            <React.Fragment key={`course-${index}`}>
                                                {this.renderProductSummary('Course', product, index === 0)}
                                            </React.Fragment>
                                        ))
                                    }
                                    {state.summary.accommodation && state.summary.accommodation.length > 0 && 
                                        state.summary.accommodation.map((product: any, index: number) => (
                                            <React.Fragment key={`accommodation-${index}`}>
                                                {this.renderProductSummary('Accommodation', product, index === 0)}
                                            </React.Fragment>
                                        ))
                                    }
                                    {state.summary.transfer && state.summary.transfer.length > 0 && 
                                        state.summary.transfer.map((product: any, index: number) => (
                                            <React.Fragment key={`transfer-${index}`}>
                                                {this.renderProductSummary('Transfer', product, index === 0)}
                                            </React.Fragment>
                                        ))
                                    }
                                    {state.summary.addOns && state.summary.addOns.length > 0 && 
                                        state.summary.addOns.map((product: any, index: number) => (
                                            <React.Fragment key={`addons-${index}`}>
                                                {this.renderProductSummary('AddOns', product, index === 0)}
                                            </React.Fragment>
                                        ))
                                    }
                                    {state.summary.package && state.summary.package.length > 0 && 
                                        state.summary.package.map((product: any, index: number) => {
                                            const isExpanded = this.state.packageExpandedIndices.includes(index);
                                            const hasBreakdown = product.breakdownLines && product.breakdownLines.length > 0;
                                            const currency = (product.price || '').match(/[A-Z]{3}/)?.[0] || 'EUR';
                                            return (
                                                <div key={`package-${index}`} className="summary-section summary-package">
                                                    {index === 0 && (
                                                        <div className="summary-section-header">
                                                            <Title level={4} style={{ margin: 0 }}>Package</Title>
                                                        </div>
                                                    )}
                                                    <div className="summary-package-header">
                                                        <span
                                                            className="summary-package-expand"
                                                            onClick={() => {
                                                                if (!hasBreakdown) return;
                                                                const next = isExpanded
                                                                    ? this.state.packageExpandedIndices.filter(i => i !== index)
                                                                    : [...this.state.packageExpandedIndices, index];
                                                                this.setState({ packageExpandedIndices: next });
                                                            }}
                                                            style={{ cursor: hasBreakdown ? 'pointer' : 'default', marginRight: 8 }}
                                                        >
                                                            {hasBreakdown ? (isExpanded ? <CaretDownOutlined /> : <CaretRightOutlined />) : <span style={{ width: 14, display: 'inline-block' }} />}
                                                        </span>
                                                        <span className="summary-package-name">{product.name}</span>
                                                        <span className="summary-package-total">{product.price || `0.00 ${currency}`}</span>
                                                    </div>
                                                    {hasBreakdown && isExpanded && (
                                                        <div className="summary-package-breakdown">
                                                            {product.breakdownLines.map((line: any, lineIndex: number) => (
                                                                <div key={lineIndex} className="summary-package-breakdown-row">
                                                                    <span className="summary-package-breakdown-pct">{line.percentage}</span>
                                                                    <span className="summary-package-breakdown-type">{line.type}</span>
                                                                    <span className="summary-package-breakdown-name">{line.offeringName}</span>
                                                                    <span className="summary-package-breakdown-total">{line.lineTotal}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {product.priceByDateRange && product.priceByDateRange.length > 0 && (
                                                        <div className="summary-two-column" style={{ marginTop: 8 }}>
                                                            <div className="summary-col-left-2">
                                                                <div className="summary-row-label-2">Price Breakdown:</div>
                                                                {product.priceByDateRange.map((range: any, rangeIndex: number) => (
                                                                    <div key={rangeIndex} className="summary-row-label-2 summary-price-breakdown-item summary-price-breakdown-value">
                                                                        <span>{range.dateFrom} to {range.dateTo}:</span>
                                                                        <SummaryEarlyBirdInline earlyBird={range.earlyBird} earlyBirdName={range.earlyBirdName} />
                                                                        <SummaryChangeStatusInline changeStatus={range.changeStatus} />
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            <div className="summary-col-right-2">
                                                                <div className="summary-row-price-2"></div>
                                                                {product.priceByDateRange.map((range: any, rangeIndex: number) => (
                                                                    <div key={rangeIndex} className="summary-row-price-2 summary-price-breakdown-item">
                                                                        {range.price}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div className="summary-two-column">
                                                        <div className="summary-col-left-2">
                                                            <div className="summary-row-label-2">Tax Code</div>
                                                            <div className="summary-row-label-2">Tax Amount</div>
                                                            <div className="summary-row-label-2">Commission Amount</div>
                                                        </div>
                                                        <div className="summary-col-right-2">
                                                            <div className="summary-row-price-2">{String(product.taxCode ?? '').trim() && String(product.taxCode).trim() !== '?' ? String(product.taxCode).trim() : 'Not Applied'}</div>
                                                            <div className="summary-row-price-2" style={{ color: '#fa8c16' }}>{product.taxAmount || '0.00 ' + currency}</div>
                                                            <div className="summary-row-price-2" style={{ color: '#fa8c16' }}>{product.commissionAmount || '0.00 ' + currency}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    }
                                    {state.summary.fees && state.summary.fees.length > 0 && 
                                        state.summary.fees.map((fee: any, index: number) => (
                                            <React.Fragment key={`fees-${index}`}>
                                                {this.renderProductSummary('Fees', fee, index === 0)}
                                            </React.Fragment>
                                        ))
                                    }

                                    {(state.summary.totalGrossPrice || state.summary.totalNetPrice || state.summary.totalPrice) && (
                                        <div className="summary-section total-price">
                                            {state.summary.totalGrossPrice && (
                                                <div className="summary-total-row">
                                                    <span className="summary-total-label">Total Gross Price</span>
                                                    <span className="summary-total-value">{state.summary.totalGrossPrice}</span>
                                                </div>
                                            )}
                                            {state.summary.totalNetPrice && (
                                                <div className="summary-total-row">
                                                    <span className="summary-total-label">Total Net Price</span>
                                                    <span className="summary-total-value">{state.summary.totalNetPrice}</span>
                                                </div>
                                            )}
                                            {!state.summary.totalGrossPrice && !state.summary.totalNetPrice && state.summary.totalPrice && (
                                                <div className="summary-total-row">
                                                    <span className="summary-total-label">Total Price</span>
                                                    <span className="summary-total-value">{state.summary.totalPrice}</span>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {state.summary.invalidProducts && state.summary.invalidProducts.length > 0 && (
                                        <div className="summary-section summary-invalid-section">
                                            <div className="summary-section-heading">Invalid Products</div>
                                            <div className="summary-invalid-grid">
                                                {state.summary.invalidProducts.map((product, index) => (
                                                    <React.Fragment key={index}>
                                                        <div className="summary-invalid-label">{product.name}</div>
                                                        <div className="summary-invalid-message">{product.errorMessage}</div>
                                                    </React.Fragment>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {state.summary.errors && state.summary.errors.length > 0 && (
                                        <div className="summary-section summary-errors-section">
                                            <div className="summary-section-heading">Errors</div>
                                            <div className="summary-errors-list">
                                                {state.summary.errors.map((error, index) => (
                                                    <div key={index} className="summary-error-item">
                                                        {error}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {state.summary.warnings && state.summary.warnings.length > 0 && (
                                        <div className="summary-section summary-warnings-section">
                                            <div className="summary-section-heading">Warnings</div>
                                            <div className="summary-warnings-list">
                                                {state.summary.warnings.map((warning, index) => (
                                                    <div key={index} className="summary-warning-item">
                                                        {warning}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <Text type="secondary">Click "Calculate" to see the summary</Text>
                            )}
                        </Card>
                    </Col>
                </Row>
            </div>
        );
    }

    renderProductSummary = (title: string, summary: any, showHeader: boolean = true) => {
        const extractCurrency = (priceStr: string): string => {
            const match = priceStr.match(/[A-Z]{3}/);
            return match ? match[0] : 'EUR';
        };
        
        const currency = extractCurrency(summary.price || '0.00 EUR');
        
        const isThreeColumn = title === 'Course' || title === 'Accommodation';
        
        const appliedPromotionCode = this.props.state.summary?.appliedPromotionCode?.trim();
        
        const dedupeDiscounts = (items: any[]): any[] => {
            const seen = new Set<string>();
            return items.filter(d => {
                const key = `${d.name || ''}|${d.amount || ''}|${d.type || ''}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });
        };
        const allDiscounts = dedupeDiscounts([
            ...(summary.discounts || []),
            ...(summary.periodDiscounts || []),
        ]);
        
        const getDiscountBadgeLabel = (type: string): string => {
            const t = (type || '').toLowerCase();
            if (t === 'earlybird' || t === 'early bird') return 'Early Bird';
            if (t === 'marketdiscount' || t === 'market') return 'Market Discount';
            if (t === 'specialoffer' || t === 'special offer') return 'Special Offer';
            return '';
        };

        const formatTaxCode = (raw: any): string => {
            const v = String(raw ?? '').trim();
            if (!v || v === '?' || v.toLowerCase() === 'xxxx') return 'Not Applied';
            return v;
        };
        
        if (isThreeColumn) {
            return (
                <div className="summary-section">
                    {showHeader && (
                        <div className="summary-section-header">
                            <div>
                                <Title level={4} style={{ margin: 0 }}>{title}</Title>
                                {summary.name && <div style={{ fontWeight: 600, marginTop: 4 }}>{summary.name}</div>}
                            </div>
                            <span className="summary-section-price">
                                {summary.units ? '' : (summary.price || '0.00 ' + currency)}
                            </span>
                        </div>
                    )}
                    {!showHeader && summary.name && (
                        <div className="summary-section-header" style={{ marginTop: 8 }}>
                            <div>
                                <div style={{ fontWeight: 600 }}>{summary.name}</div>
                            </div>
                            <span className="summary-section-price">
                                {summary.units ? '' : (summary.price || '0.00 ' + currency)}
                            </span>
                        </div>
                    )}
                    <div className="summary-row-layout">
                        {summary.units && (
                            <div className="summary-row">
                                <div className="summary-row-label">Units:</div>
                                <div className="summary-row-value">
                                    Major {summary.units.majorPrice || '0.00 ' + currency}{summary.units.minor > 0 ? ' Minor - ' + (summary.units.minorPrice || '0.00 ' + currency) : ''}
                                </div>
                                <div className="summary-row-price">{summary.price || '0.00 ' + currency}</div>
                            </div>
                        )}
                        {allDiscounts.length > 0 && allDiscounts.map((discount: any, index: number) => (
                            <div key={index} className="summary-row">
                                <div className="summary-row-label"></div>
                                <div className="summary-row-value">
                                    <span className="summary-discount-name">{discount.name}</span>
                                    {discount.type && getDiscountBadgeLabel(discount.type) && (
                                        <span
                                            className={getDiscountBadgePillClass(discount.type)}
                                            style={{ marginLeft: '8px' }}
                                        >
                                            {getDiscountBadgeLabel(discount.type)}
                                        </span>
                                    )}
                                    {normalizeDiscountKind(discount.type) === 'specialoffer' && appliedPromotionCode ? (
                                        <span className="summary-applied-promo-code"> [{appliedPromotionCode}]</span>
                                    ) : null}
                                </div>
                                <div className="summary-row-price">
                                    <span className="summary-discount-amount" style={{ color: '#52c41a' }}>
                                        - {discount.amount}
                                    </span>
                                </div>
                            </div>
                        ))}
                        {summary.priceByDateRange && summary.priceByDateRange.length > 0 && (
                            <>
                                <div className="summary-row">
                                    <div className="summary-row-label">Price Breakdown:</div>
                                    <div className="summary-row-value"></div>
                                    <div className="summary-row-price"></div>
                                </div>
                                <div className="summary-price-breakdown-items">
                                    {summary.priceByDateRange.map((range: any, index: number) => (
                                        <div key={index} className="summary-row">
                                            <div className="summary-row-label"></div>
                                            <div className="summary-row-value summary-price-breakdown-value">
                                                <span>{range.dateFrom} to {range.dateTo}:</span>
                                                <SummaryEarlyBirdInline earlyBird={range.earlyBird} earlyBirdName={range.earlyBirdName} />
                                                <SummaryChangeStatusInline changeStatus={range.changeStatus} />
                                            </div>
                                            <div className="summary-row-price">{range.price}</div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                        <div className="summary-row">
                            <div className="summary-row-label">Gross Amount:</div>
                            <div className="summary-row-value"></div>
                            <div className="summary-row-price">{summary.grossAmount || '-'}</div>
                        </div>
                        <div className="summary-row">
                            <div className="summary-row-label">Commission Amount:</div>
                            <div className="summary-row-value"></div>
                            <div className="summary-row-price" style={{ color: '#fa8c16' }}>{summary.commissionAmount || '0.00 ' + currency}</div>
                        </div>
                        <div className="summary-row">
                            <div className="summary-row-label">Net Amount:</div>
                            <div className="summary-row-value"></div>
                            <div className="summary-row-price">{summary.netAmount || '-'}</div>
                        </div>
                        <div className="summary-row">
                            <div className="summary-row-label">Tax Code:</div>
                            <div className="summary-row-value"></div>
                            <div className="summary-row-price">{formatTaxCode(summary.taxCode)}</div>
                        </div>
                        <div className="summary-row">
                            <div className="summary-row-label">Tax Amount:</div>
                            <div className="summary-row-value"></div>
                            <div className="summary-row-price" style={{ color: '#fa8c16' }}>{summary.taxAmount || '0.00 ' + currency}</div>
                        </div>
                    </div>
                </div>
            );
        } else {
            return (
                <div className="summary-section">
                    {showHeader && (
                        <div className="summary-section-header">
                            <div>
                                <Title level={4} style={{ margin: 0 }}>{title}</Title>
                                {summary.name && <div style={{ fontWeight: 600, marginTop: 4 }}>{summary.name}</div>}
                            </div>
                            <span className="summary-section-price">{summary.price || '0.00 ' + currency}</span>
                        </div>
                    )}
                    {!showHeader && summary.name && (
                        <div className="summary-section-header" style={{ marginTop: 8 }}>
                            <div>
                                <div style={{ fontWeight: 600 }}>{summary.name}</div>
                            </div>
                            <span className="summary-section-price">{summary.price || '0.00 ' + currency}</span>
                        </div>
                    )}
                    <div className="summary-two-column">
                        <div className="summary-col-left-2">
                            <div className="summary-row-label-2">Price</div>
                            {allDiscounts.length > 0 && allDiscounts.map((discount: any, index: number) => (
                                <div key={index} className="summary-row-label-2">
                                    <span className="summary-discount-name">{discount.name}</span>
                                    {discount.type && getDiscountBadgeLabel(discount.type) && (
                                        <span
                                            className={getDiscountBadgePillClass(discount.type)}
                                            style={{ marginLeft: '8px' }}
                                        >
                                            {getDiscountBadgeLabel(discount.type)}
                                        </span>
                                    )}
                                    {normalizeDiscountKind(discount.type) === 'specialoffer' && appliedPromotionCode ? (
                                        <span className="summary-applied-promo-code"> [{appliedPromotionCode}]</span>
                                    ) : null}
                                </div>
                            ))}
                            {summary.priceByDateRange && summary.priceByDateRange.length > 0 && (
                                <>
                                    <div className="summary-row-label-2">Price Breakdown:</div>
                                    {summary.priceByDateRange.map((range: any, index: number) => (
                                        <div key={index} className="summary-row-label-2 summary-price-breakdown-item summary-price-breakdown-value">
                                            <span>{range.dateFrom} to {range.dateTo}:</span>
                                            <SummaryEarlyBirdInline earlyBird={range.earlyBird} earlyBirdName={range.earlyBirdName} />
                                            <SummaryChangeStatusInline changeStatus={range.changeStatus} />
                                        </div>
                                    ))}
                                </>
                            )}
                            <div className="summary-row-label-2">Gross Amount</div>
                            <div className="summary-row-label-2">Commission Amount</div>
                            <div className="summary-row-label-2">Net Amount</div>
                            <div className="summary-row-label-2">Tax Code</div>
                            <div className="summary-row-label-2">Tax Amount</div>
                        </div>
                        
                        <div className="summary-col-right-2">
                            <div className="summary-row-price-2">{summary.price || '0.00 ' + currency}</div>
                            {allDiscounts.length > 0 && allDiscounts.map((discount: any, index: number) => (
                                <div key={index} className="summary-row-price-2">
                                    <span className="summary-discount-amount" style={{ color: '#52c41a' }}>
                                        - {discount.amount}
                                    </span>
                                </div>
                            ))}
                            {summary.priceByDateRange && summary.priceByDateRange.length > 0 && (
                                <>
                                    <div className="summary-row-price-2"></div>
                                    {summary.priceByDateRange.map((range: any, index: number) => (
                                        <div key={index} className="summary-row-price-2 summary-price-breakdown-item">
                                            {range.price}
                                        </div>
                                    ))}
                                </>
                            )}
                            <div className="summary-row-price-2">{summary.grossAmount || '-'}</div>
                            <div className="summary-row-price-2" style={{ color: '#fa8c16' }}>
                                {summary.commissionAmount || '0.00 ' + currency}
                            </div>
                            <div className="summary-row-price-2">{summary.netAmount || '-'}</div>
                            <div className="summary-row-price-2">{formatTaxCode(summary.taxCode)}</div>
                            <div className="summary-row-price-2" style={{ color: '#fa8c16' }}>
                                {summary.taxAmount || '0.00 ' + currency}
                            </div>
                        </div>
                    </div>
                </div>
            );
        }
    };
}

const mapStateToProps = (state: any) => {
    const calculatorState = state.reducer || {};
    return {
        state: {
            ...calculatorState,
            countries: calculatorState.countries || [],
            currencies: calculatorState.currencies || [],
            centres: calculatorState.centres || [],
            schools: calculatorState.schools || [],
            bookingSystems: calculatorState.bookingSystems || [],
            agents: calculatorState.agents || [],
            agentOffices: calculatorState.agentOffices || [],
            divisions: calculatorState.divisions ?? [],
            divisionId: calculatorState.divisionId,
            bookingSystemId: calculatorState.bookingSystemId,
            isLoadingReferenceData: calculatorState.isLoadingReferenceData || false,
            products: calculatorState.products || [],
            selectedProductType: calculatorState.selectedProductType || 'Course',
            agentType: calculatorState.agentType || 'Agent',
            excludeCommissions: calculatorState.excludeCommissions || false,
            earlyBirds: calculatorState.earlyBirds || false,
            marketDiscount: calculatorState.marketDiscount || false,
            productSearchTerm: calculatorState.productSearchTerm || '',
            isCalculating: calculatorState.isCalculating || false,
        },
    };
};

export const Page = connect(mapStateToProps)(CalculatorPage);